/**
 * ArtifactPanel - Display rich content outside of chat context
 *
 * This panel displays "artifacts" - structured data that would be too large
 * or complex to include in Claude's message context. Examples:
 * - Photo galleries (loaded via URLs, not base64)
 * - Charts and visualizations
 * - Large data tables
 * - PDF previews
 *
 * This is similar to how Claude.ai handles artifacts - they're rendered
 * separately from the chat, so they don't count against token limits.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { X, Image, BarChart2, Table, FileText, ChevronLeft, ChevronRight, Mail, Check, ZoomIn, Download, Minimize2, Maximize2, PanelRightClose, ChevronDown, ChevronUp, AlertTriangle, Clock, DollarSign, Package, Wrench, Layers, Calendar, User, Building2, Hash, Settings, Boxes, TrendingUp, TrendingDown, Truck, ShoppingCart, Paperclip, ExternalLink, Eye } from 'lucide-react'
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ReferenceLine, ZAxis
} from 'recharts'
import EmailComposeModal from './EmailComposeModal'
import EmailComposeArtifact from './EmailComposeArtifact'
import AttachmentViewer, { AttachmentList } from '../attachments/AttachmentViewer'
import useAuth from '../../hooks/useAuth'

/**
 * Hook to load an authenticated image and return a blob URL
 */
function useAuthenticatedImage(imageUrl) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!imageUrl) return

    const loadImage = async () => {
      try {
        setLoading(true)
        setError(false)

        const token = localStorage.getItem('amtrend_token')
        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setBlobUrl(url)
        setLoading(false)
      } catch (err) {
        console.error('Failed to load image:', imageUrl, err)
        setError(true)
        setLoading(false)
      }
    }

    loadImage()

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [imageUrl])

  return { blobUrl, loading, error }
}

/**
 * Photo Gallery Artifact - loads images via URLs
 */
function PhotoGalleryArtifact({ data, onEmailRequest }) {
  const [selectedPhotos, setSelectedPhotos] = useState([])
  const [lightboxPhoto, setLightboxPhoto] = useState(null)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [showEmailModal, setShowEmailModal] = useState(false)

  // Flatten all photos for navigation
  const allPhotos = data.lines?.flatMap(line => line.photos) || []

  const selectAll = () => {
    setSelectedPhotos([...allPhotos])
  }

  const deselectAll = () => {
    setSelectedPhotos([])
  }

  const allSelected = allPhotos.length > 0 && selectedPhotos.length === allPhotos.length

  const toggleSelect = (photo) => {
    setSelectedPhotos(prev => {
      const isSelected = prev.some(p => p.xfile_ref_num === photo.xfile_ref_num)
      if (isSelected) {
        return prev.filter(p => p.xfile_ref_num !== photo.xfile_ref_num)
      }
      return [...prev, photo]
    })
  }

  const openLightbox = (photo) => {
    const index = allPhotos.findIndex(p => p.xfile_ref_num === photo.xfile_ref_num)
    setLightboxIndex(index >= 0 ? index : 0)
    setLightboxPhoto(photo)
  }

  const handleEmailSelected = () => {
    if (selectedPhotos.length === 0) return
    // Open the email compose modal instead of sending a message to Claude
    setShowEmailModal(true)
  }

  const handleEmailSuccess = (result) => {
    // Clear selection after successful send
    setSelectedPhotos([])
    // Don't notify Claude - the email was sent directly via M365, no need to involve Claude
    // This avoids confusing messages like "I sent photos" which Claude interprets as a user request
  }

  if (!data.lines || data.lines.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        <Image size={48} className="mx-auto mb-2 opacity-50" />
        <p>{data.message || 'No photos found'}</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Photos for Order {data.order_num}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{data.total_photos} photos</p>
          </div>
          {selectedPhotos.length > 0 && (
            <button
              onClick={handleEmailSelected}
              className="flex items-center gap-2 px-3 py-1.5 bg-amtrend-blue text-white text-sm rounded-lg hover:bg-blue-700"
            >
              <Mail size={16} />
              Email {selectedPhotos.length}
            </button>
          )}
        </div>
        {/* Selection controls */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={allSelected ? deselectAll : selectAll}
            className="text-sm text-amtrend-blue hover:text-blue-700 dark:hover:text-blue-400 font-medium"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          {selectedPhotos.length > 0 && selectedPhotos.length < allPhotos.length && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={deselectAll}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Clear Selection
              </button>
            </>
          )}
          {selectedPhotos.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
              {selectedPhotos.length} of {allPhotos.length} selected
            </span>
          )}
        </div>
      </div>

      {/* Gallery Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {data.lines.map((line) => (
          <div key={line.order_line} className="bg-gray-50 dark:bg-surface-dark-overlay rounded-lg p-4">
            <div className="mb-3">
              <h4 className="font-medium text-gray-700 dark:text-gray-300">
                Line {line.order_line}
                {line.part_num && (
                  <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">{line.part_num}</span>
                )}
              </h4>
              {line.xpart_num && (
                <p className="text-xs text-gray-400 dark:text-gray-500">Customer P/N: {line.xpart_num}</p>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {line.photos.map((photo) => (
                <PhotoThumbnail
                  key={photo.xfile_ref_num}
                  photo={photo}
                  isSelected={selectedPhotos.some(p => p.xfile_ref_num === photo.xfile_ref_num)}
                  onSelect={() => toggleSelect(photo)}
                  onClick={() => openLightbox(photo)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <Lightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxPhoto(null)}
          onNext={() => {
            const next = lightboxIndex + 1
            if (next < allPhotos.length) {
              setLightboxIndex(next)
              setLightboxPhoto(allPhotos[next])
            }
          }}
          onPrev={() => {
            const prev = lightboxIndex - 1
            if (prev >= 0) {
              setLightboxIndex(prev)
              setLightboxPhoto(allPhotos[prev])
            }
          }}
          hasNext={lightboxIndex < allPhotos.length - 1}
          hasPrev={lightboxIndex > 0}
        />
      )}

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        orderNum={data.order_num}
        selectedPhotos={selectedPhotos}
        onSuccess={handleEmailSuccess}
      />
    </div>
  )
}

/**
 * Photo thumbnail component - uses authenticated fetch for images
 */
function PhotoThumbnail({ photo, isSelected, onSelect, onClick }) {
  // Use the hook to load the image with authentication
  const imageUrl = photo.thumbnail_url || photo.full_url
  const { blobUrl, loading, error } = useAuthenticatedImage(imageUrl)

  return (
    <div className="relative group">
      {/* Selection checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-amtrend-blue border-amtrend-blue text-white'
            : 'bg-white/80 border-gray-300 dark:border-gray-600 hover:border-amtrend-blue'
        }`}
      >
        {isSelected && <Check size={14} />}
      </button>

      {/* Photo */}
      <div
        onClick={onClick}
        className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-amtrend-blue/50 transition-all"
      >
        {loading && (
          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse" />
        )}

        {error ? (
          <div className="w-full h-32 bg-gray-100 dark:bg-surface-dark-overlay flex items-center justify-center">
            <span className="text-gray-400 dark:text-gray-500 text-sm">Failed to load</span>
          </div>
        ) : blobUrl ? (
          <img
            src={blobUrl}
            alt={photo.filename}
            className={`w-full h-32 object-cover ${loading ? 'opacity-0' : 'opacity-100'}`}
          />
        ) : (
          <div className="w-full h-32 bg-gray-100 dark:bg-surface-dark-overlay" />
        )}

        {/* Zoom overlay */}
        {!error && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        )}

        {/* Filename */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
          <p className="text-white text-xs truncate">{photo.filename}</p>
        </div>
      </div>
    </div>
  )
}

/**
 * Lightbox for full-size viewing - uses authenticated fetch
 */
function Lightbox({ photo, onClose, onNext, onPrev, hasNext, hasPrev }) {
  const imageUrl = photo.full_url || photo.thumbnail_url
  const { blobUrl, loading, error } = useAuthenticatedImage(imageUrl)

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
      >
        <X size={24} />
      </button>

      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev() }}
          className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext() }}
          className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
        >
          <ChevronRight size={32} />
        </button>
      )}

      <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh]">
        {loading && (
          <div className="w-96 h-64 bg-gray-800 animate-pulse rounded-lg flex items-center justify-center">
            <span className="text-gray-400 dark:text-gray-500">Loading...</span>
          </div>
        )}

        {error ? (
          <div className="w-96 h-64 bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-gray-400 dark:text-gray-500">Failed to load image</span>
          </div>
        ) : blobUrl && (
          <img
            src={blobUrl}
            alt={photo.filename}
            className={`max-w-full max-h-[85vh] object-contain rounded-lg ${loading ? 'hidden' : ''}`}
          />
        )}

        <div className="mt-3 text-center">
          <p className="text-white font-medium">{photo.filename}</p>
          {photo.description && (
            <p className="text-gray-300 text-sm mt-1">{photo.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * EpicorOpenButtons — dual View (Tracker) / Edit (Entry) links to Epicor Kinetic.
 * Rendered in each artifact header when entry or tracker URLs are present.
 */
function EpicorOpenButtons({ entryUrl, trackerUrl }) {
  if (!entryUrl && !trackerUrl) return null
  return (
    <div className="inline-flex items-center gap-1">
      {trackerUrl && (
        <a
          href={trackerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
            text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800
            hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors
            border border-gray-200 dark:border-gray-600"
        >
          <Eye size={11} />
          View
        </a>
      )}
      {entryUrl && (
        <a
          href={entryUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
            text-amtrend-blue bg-blue-50 dark:bg-blue-900/20
            hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors
            border border-blue-200 dark:border-blue-800"
        >
          <ExternalLink size={11} />
          Edit
        </a>
      )}
    </div>
  )
}

/**
 * Quote Detail Artifact - comprehensive quote view with worksheet
 */
function QuoteDetailArtifact({ data, onSendMessage }) {
  const { user } = useAuth()
  const canEditQuotes = user?.features?.can_edit_quotes === true

  const [expandedLines, setExpandedLines] = useState(new Set())
  const [commentExpanded, setCommentExpanded] = useState(false)
  const [edits, setEdits] = useState({})   // { [quoteLine]: { qty, unit_price } }
  const [saving, setSaving] = useState(false)

  const toggleLine = (lineNum) => {
    setExpandedLines(prev => {
      const next = new Set(prev)
      if (next.has(lineNum)) {
        next.delete(lineNum)
      } else {
        next.add(lineNum)
      }
      return next
    })
  }

  const expandAll = () => {
    const allLines = data.lines?.map(l => l.quote_line) || []
    setExpandedLines(new Set(allLines))
  }

  const collapseAll = () => {
    setExpandedLines(new Set())
  }

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatHours = (val) => {
    if (!val) return '0h'
    return `${val.toFixed(1)}h`
  }

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Ordered': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'Expired': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  // ── Editing helpers ──────────────────────────────────────────────
  const updateLineEdit = (quoteLine, field, value) => {
    setEdits(prev => ({
      ...prev,
      [quoteLine]: { ...prev[quoteLine], [field]: value }
    }))
  }

  const getLineValue = (line, field) => {
    const editVal = edits[line.quote_line]?.[field]
    return editVal !== undefined ? editVal : line[field]
  }

  const isDirty = Object.keys(edits).some(ql => {
    const line = data.lines?.find(l => l.quote_line === Number(ql))
    if (!line) return false
    const e = edits[ql]
    return (e.qty !== undefined && e.qty !== line.qty) ||
           (e.unit_price !== undefined && e.unit_price !== line.unit_price)
  })

  const getChangedLines = () => {
    return Object.entries(edits)
      .filter(([ql, e]) => {
        const line = data.lines?.find(l => l.quote_line === Number(ql))
        if (!line) return false
        return (e.qty !== undefined && e.qty !== line.qty) ||
               (e.unit_price !== undefined && e.unit_price !== line.unit_price)
      })
      .map(([ql, e]) => {
        const line = data.lines.find(l => l.quote_line === Number(ql))
        return {
          quote_line: Number(ql),
          part_num: line.part_num,
          ...(e.qty !== undefined && e.qty !== line.qty ? { old_qty: line.qty, new_qty: e.qty } : {}),
          ...(e.unit_price !== undefined && e.unit_price !== line.unit_price ? { old_price: line.unit_price, new_price: e.unit_price } : {}),
        }
      })
  }

  // ── Live margin calculations ─────────────────────────────────────
  const calcLineMargin = (line) => {
    const price = getLineValue(line, 'unit_price')
    const cost = line.costs?.total ?? 0
    if (!price || price <= 0) return null
    return ((price - cost) / price) * 100
  }

  const calcOverallMargin = () => {
    const allLines = data.lines || []
    let totalRev = 0, totalCost = 0
    for (const line of allLines) {
      const qty = getLineValue(line, 'qty') || 0
      const price = getLineValue(line, 'unit_price') || 0
      const cost = line.costs?.total ?? 0
      totalRev += qty * price
      totalCost += qty * cost
    }
    if (totalRev <= 0) return null
    return ((totalRev - totalCost) / totalRev) * 100
  }

  const calcTotalValue = () => {
    const allLines = data.lines || []
    return allLines.reduce((sum, l) =>
      sum + (getLineValue(l, 'qty') || 0) * (getLineValue(l, 'unit_price') || 0), 0)
  }

  const marginColorClass = (pct) => {
    if (pct === null || pct === undefined) return 'text-gray-400 dark:text-gray-500'
    if (pct >= 45) return 'text-green-600 dark:text-green-400'
    if (pct >= 40) return 'text-amber-600 dark:text-amber-400'
    return 'text-red-600 dark:text-red-400'
  }

  // ── Message handlers ─────────────────────────────────────────────
  const handleSaveChanges = () => {
    if (!onSendMessage || !isDirty) return
    setSaving(true)
    const changes = getChangedLines()
    const changeDesc = changes.map(c => {
      const parts = []
      if (c.new_price !== undefined) parts.push(`price $${c.old_price?.toFixed(2)} → $${c.new_price?.toFixed(2)}`)
      if (c.new_qty !== undefined) parts.push(`qty ${c.old_qty} → ${c.new_qty}`)
      return `Line ${c.quote_line} (${c.part_num}): ${parts.join(', ')}`
    }).join('\n')

    const overallPct = calcOverallMargin()
    const msg = `Please update Quote ${data.quote_num} with these pricing changes:\n${changeDesc}\n\nOverall margin after changes: ${overallPct?.toFixed(1) ?? 'N/A'}%`
    onSendMessage(msg)
    setTimeout(() => setSaving(false), 3000)
  }

  const handleAdvanceWorkflow = () => {
    if (!onSendMessage) return
    setSaving(true)
    const wf = data.workflow
    let msg = ''

    if (isDirty) {
      const changes = getChangedLines()
      const changeDesc = changes.map(c => {
        const parts = []
        if (c.new_price !== undefined) parts.push(`price $${c.old_price?.toFixed(2)} → $${c.new_price?.toFixed(2)}`)
        if (c.new_qty !== undefined) parts.push(`qty ${c.old_qty} → ${c.new_qty}`)
        return `Line ${c.quote_line} (${c.part_num}): ${parts.join(', ')}`
      }).join('\n')
      msg = `Please update Quote ${data.quote_num} with these changes:\n${changeDesc}\n\nThen advance the workflow from "${wf.active_task_name}" to the next stage.`
    } else {
      msg = `Please advance Quote ${data.quote_num} workflow from "${wf.active_task_name}" to the next stage.`
    }
    onSendMessage(msg)
    setTimeout(() => setSaving(false), 3000)
  }

  // ── Rendering ────────────────────────────────────────────────────
  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, summary, lines, workflow } = data
  const isEditable = header.status === 'Open' && canEditQuotes
  const currentTotalValue = isDirty ? calcTotalValue() : summary.total_value
  const currentMarginPct = isDirty ? calcOverallMargin() : summary.margin_pct
  const currentMargin = isDirty
    ? (currentTotalValue - (data.lines || []).reduce((s, l) => s + (getLineValue(l, 'qty') || 0) * (l.costs?.total ?? 0), 0))
    : summary.margin

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Quote Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status & Workflow Badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(header.status)}`}>
              {header.status}
            </span>
            {workflow && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                {workflow.active_task_name}
              </span>
            )}
            {header.potentially_incorrect_costing && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 flex items-center gap-1">
                <AlertTriangle size={12} />
                Pre-12/1 Costing
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {header.order_num && (
              <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                → Order #{header.order_num}
              </span>
            )}
            <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-3">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-1">
            <Building2 size={14} />
            <span>Customer</span>
          </div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{header.customer_name}</p>
          {header.ship_to_name && header.ship_to_name !== header.customer_name && (
            <p className="text-sm text-gray-600 dark:text-gray-400">Ship To: {header.ship_to_name}</p>
          )}
        </div>

        {/* Key Dates */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Quote Date
            </div>
            <p className="font-medium">{formatDate(header.quote_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Clock size={12} />
              Expires
            </div>
            <p className="font-medium">{formatDate(header.expiration_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <User size={12} />
              Sales Rep
            </div>
            <p className="font-medium">{header.sales_rep || '-'}</p>
          </div>
        </div>

        {/* Reference & PO if present */}
        {(header.reference || header.po_num) && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex gap-4 text-sm">
            {header.reference && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Reference:</span>
                <span className="ml-1 font-medium">{header.reference}</span>
              </div>
            )}
            {header.po_num && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">PO:</span>
                <span className="ml-1 font-medium">{header.po_num}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Stats — recalculates live when edits are dirty */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {(() => {
          const isQuoteCosted = (summary.total_cost != null && summary.total_cost > 0) || summary.margin_pct != null
          return (
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Value</p>
                <p className={`font-bold text-lg text-gray-900 dark:text-gray-100 ${isDirty ? 'italic' : ''}`}>
                  {formatCurrency(currentTotalValue)}
                </p>
              </div>
              <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Cost</p>
                {isQuoteCosted ? (
                  <p className="font-bold text-lg text-gray-700 dark:text-gray-300">{formatCurrency(summary.total_cost)}</p>
                ) : (
                  <p className="font-medium text-sm text-amber-600 dark:text-amber-400">Not Costed</p>
                )}
              </div>
              <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Margin</p>
                {isQuoteCosted ? (
                  <p className={`font-bold text-lg ${isDirty ? 'italic' : ''} ${currentMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(currentMargin)}
                  </p>
                ) : (
                  <p className="font-medium text-sm text-gray-400 dark:text-gray-500">n/a</p>
                )}
              </div>
              <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Margin %</p>
                {isQuoteCosted ? (
                  <p className={`font-bold text-lg ${isDirty ? 'italic' : ''} ${marginColorClass(currentMarginPct)}`}>
                    {currentMarginPct?.toFixed(1) ?? '-'}%
                  </p>
                ) : (
                  <p className="font-medium text-sm text-gray-400 dark:text-gray-500">n/a</p>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Line Items Header */}
      <div className="px-4 py-2 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700 flex items-center justify-between">
        <h4 className="font-semibold text-gray-700 dark:text-gray-300">
          {summary.line_count} Line Item{summary.line_count !== 1 ? 's' : ''}
        </h4>
        <div className="flex gap-2">
          <button onClick={expandAll} className="text-xs text-amtrend-blue hover:text-blue-700 dark:hover:text-blue-400">
            Expand All
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <button onClick={collapseAll} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            Collapse All
          </button>
        </div>
      </div>

      {/* Line Items */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {lines?.map((line) => (
          <QuoteLineCard
            key={line.quote_line}
            line={line}
            isExpanded={expandedLines.has(line.quote_line)}
            onToggle={() => toggleLine(line.quote_line)}
            formatCurrency={formatCurrency}
            formatHours={formatHours}
            editValues={edits[line.quote_line]}
            onEditChange={(field, value) => updateLineEdit(line.quote_line, field, value)}
            calcMargin={calcLineMargin(line)}
            isEditable={isEditable}
            marginColorClass={marginColorClass}
          />
        ))}
      </div>

      {/* Comment if present - collapsible */}
      {header.comment && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-100 dark:border-yellow-800">
          <button
            onClick={() => setCommentExpanded(!commentExpanded)}
            className="w-full px-4 py-2 flex items-center justify-between hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
          >
            <span className="text-xs text-yellow-800 dark:text-yellow-300 font-medium flex items-center gap-2">
              <AlertTriangle size={12} />
              Quote Comment
            </span>
            {commentExpanded ? <ChevronUp size={14} className="text-yellow-600 dark:text-yellow-400" /> : <ChevronDown size={14} className="text-yellow-600 dark:text-yellow-400" />}
          </button>
          {commentExpanded && (
            <div className="px-4 pb-3 max-h-32 overflow-y-auto">
              <p className="text-sm text-yellow-900 dark:text-yellow-200 whitespace-pre-wrap">{header.comment}</p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons — only for Open quotes */}
      {isEditable && (
        <div className="p-3 bg-white dark:bg-surface-dark-raised border-t dark:border-gray-700 flex items-center justify-between gap-2 shrink-0">
          <div>
            {isDirty && (
              <button
                onClick={() => setEdits({})}
                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {isDirty && (
              <button
                onClick={handleSaveChanges}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            {workflow && (
              <button
                onClick={handleAdvanceWorkflow}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {workflow.next_task_name
                  ? `Advance → ${workflow.next_task_name}`
                  : 'Complete Workflow'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Individual Quote Line Card with expandable worksheet
 */
function QuoteLineCard({ line, isExpanded, onToggle, formatCurrency, formatHours, editValues, onEditChange, calcMargin, isEditable, marginColorClass }) {
  const hasWorksheet = line.worksheet?.has_detail

  // Check if line has been costed (total cost > 0 or margin_pct is set)
  const isCosted = (line.costs?.total > 0) || (line.margin_pct != null)

  // Use live-calculated margin when editing, original otherwise
  const displayMargin = calcMargin ?? line.margin_pct
  const mColor = marginColorClass ? marginColorClass(displayMargin) : (
    !isCosted ? 'text-gray-400 dark:text-gray-500' : displayMargin >= 20 ? 'text-green-600 dark:text-green-400' : displayMargin >= 10 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
  )

  // Current values (edited or original)
  const currentQty = editValues?.qty !== undefined ? editValues.qty : line.qty
  const currentPrice = editValues?.unit_price !== undefined ? editValues.unit_price : line.unit_price
  const currentExtPrice = (currentQty || 0) * (currentPrice || 0)

  // Dirty detection for this line
  const lineIsDirty = editValues && (
    (editValues.qty !== undefined && editValues.qty !== line.qty) ||
    (editValues.unit_price !== undefined && editValues.unit_price !== line.unit_price)
  )

  return (
    <div className={`bg-white dark:bg-surface-dark-raised rounded-lg border overflow-hidden ${lineIsDirty ? 'border-blue-300 ring-1 ring-blue-100 dark:border-blue-600 dark:ring-blue-900/50' : 'border-gray-200 dark:border-gray-700'}`}>
      {/* Line Header - Always visible */}
      <div
        className={`p-3 ${hasWorksheet ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-dark-overlay' : ''}`}
        onClick={hasWorksheet ? onToggle : undefined}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                Line {line.quote_line}
              </span>
              {lineIsDirty && (
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">
                  Modified
                </span>
              )}
              {line.selected && (
                <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                  Selected
                </span>
              )}
              {line.engineer_required && (
                <span className="text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-1.5 py-0.5 rounded">
                  Eng Required
                </span>
              )}
              {!isCosted && (
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                  Not Costed
                </span>
              )}
              {line.build_location && (
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Building2 size={10} />
                  {line.build_location}
                </span>
              )}
            </div>
            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{line.part_num}</p>
            {line.xpart_num && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Customer P/N: {line.xpart_num}</p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{line.description}</p>
          </div>
          <div className="text-right ml-4 shrink-0">
            <p className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(currentExtPrice)}</p>
            {/* Editable qty × price row */}
            {isEditable ? (
              <div className="flex items-center justify-end gap-1 mt-0.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={currentQty ?? ''}
                  onChange={(e) => onEditChange('qty', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-14 px-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-xs text-right dark:bg-surface-dark-overlay dark:text-gray-100 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
                  title="Quantity"
                />
                <span className="text-xs text-gray-400 dark:text-gray-500">×</span>
                <div className="relative">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">$</span>
                  <input
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={currentPrice ?? ''}
                    onChange={(e) => onEditChange('unit_price', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-20 pl-4 pr-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-xs text-right dark:bg-surface-dark-overlay dark:text-gray-100 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
                    title="Unit Price"
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">{line.qty} × {formatCurrency(line.unit_price)}</p>
            )}
            {isCosted ? (
              <p className={`text-xs font-medium ${mColor}`}>
                {displayMargin?.toFixed(1) ?? '-'}% margin
              </p>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">margin n/a</p>
            )}
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <DollarSign size={12} />
            {isCosted ? `Cost: ${formatCurrency(line.costs?.total)}` : 'Cost: n/a'}
          </span>
          {line.lead_time && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              Lead: {line.lead_time} days
            </span>
          )}
          {hasWorksheet && (
            <span className="flex items-center gap-1 ml-auto text-amtrend-blue">
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {line.worksheet.operations.length} ops, {line.worksheet.materials.length} mtls
            </span>
          )}
        </div>
      </div>

      {/* Expanded Worksheet */}
      {isExpanded && hasWorksheet && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-surface-dark">
          {/* Cost Breakdown - only show if costed */}
          {isCosted ? (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <DollarSign size={12} />
                Cost Breakdown
              </h5>
              <div className="grid grid-cols-5 gap-2 text-xs">
                <div className="bg-white dark:bg-surface-dark-overlay p-2 rounded text-center">
                  <p className="text-gray-500 dark:text-gray-400">Material</p>
                  <p className="font-medium">{formatCurrency(line.costs?.material)}</p>
                </div>
                <div className="bg-white dark:bg-surface-dark-overlay p-2 rounded text-center">
                  <p className="text-gray-500 dark:text-gray-400">Labor</p>
                  <p className="font-medium">{formatCurrency(line.costs?.labor)}</p>
                </div>
                <div className="bg-white dark:bg-surface-dark-overlay p-2 rounded text-center">
                  <p className="text-gray-500 dark:text-gray-400">Burden</p>
                  <p className="font-medium">{formatCurrency(line.costs?.burden)}</p>
                </div>
                <div className="bg-white dark:bg-surface-dark-overlay p-2 rounded text-center">
                  <p className="text-gray-500 dark:text-gray-400">Subcon</p>
                  <p className="font-medium">{formatCurrency(line.costs?.subcontract)}</p>
                </div>
                <div className="bg-white dark:bg-surface-dark-overlay p-2 rounded text-center border-2 border-gray-300 dark:border-gray-600">
                  <p className="text-gray-500 dark:text-gray-400">Total</p>
                  <p className="font-bold">{formatCurrency(line.costs?.total)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center">
                <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">Line Not Costed</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Costs and margins are not available. The worksheet may have operations/materials but costing hasn't been run.</p>
              </div>
            </div>
          )}

          {/* Operations */}
          {line.worksheet.operations.length > 0 && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <Wrench size={12} />
                Operations ({line.worksheet.operations.length})
                <span className="ml-auto font-normal text-gray-500 dark:text-gray-400">
                  Total: {formatHours(line.worksheet.summary.total_hours)}
                </span>
              </h5>
              <div className="space-y-1">
                {line.worksheet.operations.map((op, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-white dark:bg-surface-dark-overlay p-2 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 dark:text-gray-500 w-6">{op.opr_seq}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{op.op_code}</span>
                      <span className="text-gray-500 dark:text-gray-400">{op.op_desc}</span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
                      {op.resource_grp && (
                        <span className="text-gray-400 dark:text-gray-500">{op.resource_grp}</span>
                      )}
                      <span>S: {formatHours(op.setup_hours)}</span>
                      <span>P: {formatHours(op.prod_hours)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Materials */}
          {line.worksheet.materials.length > 0 && (
            <div className="p-3">
              <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <Package size={12} />
                Materials ({line.worksheet.materials.length})
                <span className="ml-auto font-normal text-gray-500 dark:text-gray-400">
                  Total: {formatCurrency(line.worksheet.summary.material_cost)}
                </span>
              </h5>
              <div className="space-y-1">
                {line.worksheet.materials.map((mtl, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-white dark:bg-surface-dark-overlay p-2 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 dark:text-gray-500 w-6">{mtl.mtl_seq}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{mtl.part_num}</span>
                      <span className="text-gray-500 dark:text-gray-400 truncate max-w-[150px]">{mtl.description}</span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
                      <span>{mtl.qty_per} {mtl.uom}</span>
                      <span>@ {formatCurrency(mtl.unit_cost)}</span>
                      <span className="font-medium">{formatCurrency(mtl.ext_cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Customer 360 Artifact - comprehensive customer dashboard
 */
function Customer360Artifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Health status colors
  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
      case 'caution': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800'
      case 'warning': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800'
      case 'critical': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-surface-dark-overlay dark:text-gray-200 dark:border-gray-700'
    }
  }

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return '✅'
      case 'caution': return '⚠️'
      case 'warning': return '🔶'
      case 'critical': return '🔴'
      default: return '❓'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { customer, health, credit, ar_aging, contacts, open_orders, open_quotes, open_invoices, summary, combined_ar, relationships } = data

  // A capped section's header must not state a count the list below it does not
  // contain. `count` is the true total; `showing` is how many rows are here.
  const sectionCount = (section) => {
    if (!section?.truncated) return section?.count ?? 0
    const total = section.count == null ? '?' : section.count.toLocaleString()
    return `${section.showing} of ${total}`
  }

  const TruncationNote = ({ showing, total }) => (
    <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
      Showing the {showing} most recent of {total == null ? 'an unknown number of' : total.toLocaleString()} —
      narrow by date to see the rest. The total above covers only the rows shown.
    </p>
  )
  const hasNetwork = relationships?.has_relationships && combined_ar && !combined_ar.error
  const networkCount = relationships?.relationship_count || combined_ar?.entity_count || 0
  const ca = combined_ar?.combined_aging || {}

  // When Bill-To network exists, use combined AR for the primary display
  const displayAR = hasNetwork ? {
    total: combined_ar.combined_total || 0,
    current: ca.current || 0,
    days_1_30: ca.aging_1_30 || 0,
    days_31_60: ca.aging_31_60 || 0,
    days_61_90: ca.aging_61_90 || 0,
    days_90_plus: ca.aging_over_90 || 0,
    past_due_total: (ca.aging_1_30 || 0) + (ca.aging_31_60 || 0) + (ca.aging_61_90 || 0) + (ca.aging_over_90 || 0),
  } : ar_aging

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Bill-To Network Banner */}
      {hasNetwork && (
        <div className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-200 dark:border-indigo-800 flex items-center gap-2">
          <span className="text-indigo-600 dark:text-indigo-400 text-xs font-bold uppercase tracking-wider">Bill-To Network</span>
          <span className="text-indigo-500 dark:text-indigo-300 text-xs">{networkCount} entities — showing aggregated AR</span>
        </div>
      )}

      {/* Customer Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Health Badge & Customer Name */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getHealthColor(health.status)}`}>
                {getHealthIcon(health.status)} {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
              </span>
              {credit.credit_hold && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  CREDIT HOLD
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{customer.name}</h2>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">Customer #{data.cust_num} • {customer.cust_id}</p>
              <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total AR Balance</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.total_ar_balance)}</p>
          </div>
        </div>

        {/* Health Alerts */}
        {health.alerts && health.alerts.length > 0 && (
          <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-300 text-sm">
              <AlertTriangle size={14} />
              <span>{health.alerts.join(' • ')}</span>
            </div>
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Open Orders</p>
            <p className="font-bold text-gray-900 dark:text-gray-100">{summary.open_order_count}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(summary.open_order_value)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Open Quotes</p>
            <p className="font-bold text-gray-900 dark:text-gray-100">{summary.open_quote_count}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(summary.open_quote_value)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Past Due</p>
            <p className={`font-bold ${summary.past_due_amount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatCurrency(summary.past_due_amount)}
            </p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Credit Avail</p>
            <p className={`font-bold ${(credit.available_credit || 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {credit.available_credit !== null ? formatCurrency(credit.available_credit) : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'ar', 'orders', 'contacts'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'ar' && 'AR Aging'}
            {tab === 'orders' && 'Orders'}
            {tab === 'contacts' && 'Contacts'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Building2 size={16} />
                Customer Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Address</p>
                  <p className="font-medium">{customer.address1}</p>
                  {customer.address2 && <p className="font-medium">{customer.address2}</p>}
                  <p className="font-medium">{customer.city}, {customer.state} {customer.zip}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Contact</p>
                  <p className="font-medium">{customer.phone || '-'}</p>
                  {customer.fax && <p className="text-gray-500 dark:text-gray-400 text-xs">Fax: {customer.fax}</p>}
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Terms</p>
                  <p className="font-medium">{customer.terms_code || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Sales Rep</p>
                  <p className="font-medium">{customer.sales_rep || '-'}</p>
                </div>
              </div>
            </div>

            {/* Credit Summary */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <DollarSign size={16} />
                Credit Summary
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Credit Limit</p>
                  <p className="font-bold">{formatCurrency(credit.credit_limit)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">AR Balance</p>
                  <p className="font-bold">{formatCurrency(credit.ar_balance)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
                  <p className={`font-bold ${(credit.available_credit || 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {credit.available_credit !== null ? formatCurrency(credit.available_credit) : 'N/A'}
                  </p>
                </div>
              </div>
              {credit.credit_limit > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>Credit Utilization</span>
                    <span>{credit.utilization_pct}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        credit.utilization_pct > 100 ? 'bg-red-500' :
                        credit.utilization_pct > 80 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(credit.utilization_pct, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Clock size={16} />
                Recent Activity
              </h3>
              <div className="space-y-2">
                {open_orders.orders.slice(0, 3).map((order) => (
                  <div key={order.order_num} className="flex items-center justify-between text-sm p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <div>
                      <span className="font-medium">Order #{order.order_num}</span>
                      {order.po_num && <span className="text-gray-500 dark:text-gray-400 ml-2">PO: {order.po_num}</span>}
                    </div>
                    <span className="font-medium">{formatCurrency(order.amount)}</span>
                  </div>
                ))}
                {open_quotes.quotes.slice(0, 2).map((quote) => (
                  <div key={quote.quote_num} className="flex items-center justify-between text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <div>
                      <span className="font-medium">Quote #{quote.quote_num}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">Exp: {formatDate(quote.expiration_date)}</span>
                    </div>
                    <span className="font-medium">{formatCurrency(quote.amount)}</span>
                  </div>
                ))}
                {open_orders.orders.length === 0 && open_quotes.quotes.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-2">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AR Aging Tab */}
        {activeTab === 'ar' && (
          <div className="space-y-4">
            {/* Aging Buckets */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">AR Aging Buckets</h3>
              <div className="grid grid-cols-5 gap-2">
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Current</p>
                  <p className="font-bold text-green-700 dark:text-green-400">{formatCurrency(displayAR.current)}</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">1-30 Days</p>
                  <p className="font-bold text-yellow-700 dark:text-yellow-300">{formatCurrency(displayAR.days_1_30)}</p>
                </div>
                <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">31-60 Days</p>
                  <p className="font-bold text-orange-700 dark:text-orange-400">{formatCurrency(displayAR.days_31_60)}</p>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">61-90 Days</p>
                  <p className="font-bold text-red-600 dark:text-red-400">{formatCurrency(displayAR.days_61_90)}</p>
                </div>
                <div className="text-center p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">90+ Days</p>
                  <p className="font-bold text-red-700 dark:text-red-400">{formatCurrency(displayAR.days_90_plus)}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t dark:border-gray-700 flex justify-between">
                <span className="font-medium">Total AR Balance</span>
                <span className="font-bold text-lg">{formatCurrency(displayAR.total)}</span>
              </div>
            </div>

            {/* Open Invoices */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Open Invoices</h3>
              {open_invoices && open_invoices.length > 0 ? (
                <div className="space-y-2">
                  {open_invoices.map((inv) => (
                    <div
                      key={inv.invoice_num}
                      className={`flex items-center justify-between text-sm p-2 rounded ${
                        inv.is_overdue ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-surface-dark-overlay'
                      }`}
                    >
                      <div>
                        <span className="font-medium">Invoice #{inv.invoice_num}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">Due: {formatDate(inv.due_date)}</span>
                        {inv.is_overdue && (
                          <span className="text-red-600 dark:text-red-400 ml-2 text-xs font-medium">
                            {inv.days_overdue} days overdue
                          </span>
                        )}
                      </div>
                      <span className={`font-medium ${inv.is_overdue ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {formatCurrency(inv.balance)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No open invoices</p>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {/* Open Orders */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                <span>Open Orders ({sectionCount(open_orders)})</span>
                <span className="text-amtrend-blue">
                  {formatCurrency(open_orders.total_value)}
                  {open_orders.truncated && <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-1">(at least)</span>}
                </span>
              </h3>
              {open_orders.truncated && <TruncationNote showing={open_orders.showing} total={open_orders.count} />}
              {open_orders.orders.length > 0 ? (
                <div className="space-y-2">
                  {open_orders.orders.map((order) => (
                    <div key={order.order_num} className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                      <div>
                        <span className="font-medium">Order #{order.order_num}</span>
                        {order.po_num && <span className="text-gray-500 dark:text-gray-400 ml-2">PO: {order.po_num}</span>}
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(order.order_date)}</p>
                      </div>
                      <span className="font-medium">{formatCurrency(order.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No open orders</p>
              )}
            </div>

            {/* Open Quotes */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
                <span>Open Quotes ({sectionCount(open_quotes)})</span>
                <span className="text-green-600 dark:text-green-400">
                  {formatCurrency(open_quotes.total_value)}
                  {open_quotes.truncated && <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-1">(at least)</span>}
                </span>
              </h3>
              {open_quotes.truncated && <TruncationNote showing={open_quotes.showing} total={open_quotes.count} />}
              {open_quotes.quotes.length > 0 ? (
                <div className="space-y-2">
                  {open_quotes.quotes.map((quote) => (
                    <div key={quote.quote_num} className="flex items-center justify-between text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <div>
                        <span className="font-medium">Quote #{quote.quote_num}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Expires: {formatDate(quote.expiration_date)}</p>
                      </div>
                      <span className="font-medium">{formatCurrency(quote.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No open quotes</p>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-3">
            {contacts && contacts.length > 0 ? (
              contacts.map((contact, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{contact.name}</h4>
                        {contact.primary && (
                          <span className="px-1.5 py-0.5 bg-amtrend-blue text-white text-xs rounded">Primary</span>
                        )}
                      </div>
                      {contact.title && <p className="text-sm text-gray-500 dark:text-gray-400">{contact.title}</p>}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    {contact.phone && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Phone: </span>
                        <span className="font-medium">{contact.phone}</span>
                      </div>
                    )}
                    {contact.cell && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Cell: </span>
                        <span className="font-medium">{contact.cell}</span>
                      </div>
                    )}
                    {contact.email && (
                      <div className="col-span-2">
                        <span className="text-gray-500 dark:text-gray-400">Email: </span>
                        <span className="font-medium text-amtrend-blue">{contact.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No contacts found for this customer
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Order Detail Artifact - comprehensive sales order view
 */
function OrderDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('lines')
  const [expandedLines, setExpandedLines] = useState(new Set())

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  // Status badge colors
  const getStatusColor = (color) => {
    switch (color) {
      case 'green': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'blue': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'yellow': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'red': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      case 'gray': return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, customer, ship_to, lines, releases, jobs, financials, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Order Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status & Order Number */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status_color)}`}>
              {status.status}
            </span>
            {status.held && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 flex items-center gap-1">
                <AlertTriangle size={12} />
                On Hold
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {header.po_num && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                PO: <span className="font-medium">{header.po_num}</span>
              </span>
            )}
            <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-3">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-1">
            <Building2 size={14} />
            <span>Customer</span>
          </div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{customer.name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {customer.city}, {customer.state} {customer.zip}
          </p>
        </div>

        {/* Key Dates */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Order Date
            </div>
            <p className="font-medium">{formatDate(header.order_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Clock size={12} />
              Need By
            </div>
            <p className="font-medium">{formatDate(header.need_by_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Package size={12} />
              Ship Via
            </div>
            <p className="font-medium">{header.ship_via || '-'}</p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Order Total</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(financials.order_total)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Shipped</p>
            <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(financials.total_shipped_value)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Invoiced</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{formatCurrency(financials.total_invoiced)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">To Ship</p>
            <p className="font-bold text-lg text-orange-600 dark:text-orange-400">{formatCurrency(financials.balance_to_ship)}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Ship Progress</span>
            <span>{summary.ship_percent}% complete</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="h-2 rounded-full bg-green-500"
              style={{ width: `${summary.ship_percent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['lines', 'releases', 'jobs', 'shipping'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'lines' && `Lines (${summary.active_line_count || summary.line_count}${summary.voided_line_count ? ` + ${summary.voided_line_count} voided` : ''})`}
            {tab === 'releases' && `Releases (${summary.release_count})`}
            {tab === 'jobs' && `Jobs (${summary.job_count})`}
            {tab === 'shipping' && 'Ship To'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Lines Tab */}
        {activeTab === 'lines' && (
          <div className="space-y-3">
            {lines?.map((line) => (
              <div key={line.line_num} className={`bg-white dark:bg-surface-dark-raised rounded-lg border p-4 ${
                line.void_line
                  ? 'border-red-200 dark:border-red-900/40 opacity-60'
                  : 'dark:border-gray-700'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                        Line {line.line_num}
                      </span>
                      {line.void_line ? (
                        <span className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                          VOIDED
                        </span>
                      ) : line.open ? (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                          Open
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                          Closed
                        </span>
                      )}
                    </div>
                    <p className={`font-medium mt-1 ${line.void_line ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>{line.part_num}</p>
                    <p className={`text-sm ${line.void_line ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>{line.description}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className={`font-semibold ${line.void_line ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>{formatCurrency(line.ext_price)}</p>
                    <p className={`text-xs ${line.void_line ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>{formatQty(line.order_qty)} × {formatCurrency(line.unit_price)}</p>
                  </div>
                </div>

                {/* Line Status — hide qty details for voided lines */}
                {line.void_line ? (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-red-100 dark:border-red-900/30 text-xs text-red-500 dark:text-red-400">
                    <span>Line voided — excluded from order totals</span>
                    {line.change_desc && <span className="text-gray-400 dark:text-gray-500">• {line.change_desc}</span>}
                  </div>
                ) : (
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <span>Ordered:</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{formatQty(line.order_qty)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>Shipped:</span>
                      <span className="font-medium text-green-600 dark:text-green-400">{formatQty(line.shipped_qty)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>Open:</span>
                      <span className="font-medium text-orange-600 dark:text-orange-400">{formatQty(line.open_qty)}</span>
                    </div>
                    {line.need_by_date && (
                      <div className="flex items-center gap-1 ml-auto">
                        <Clock size={12} />
                        <span>Need: {formatDate(line.need_by_date)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Releases Tab */}
        {activeTab === 'releases' && (
          <div className="space-y-3">
            {releases && releases.length > 0 ? (
              releases.map((rel, idx) => (
                <div key={idx} className={`bg-white dark:bg-surface-dark-raised rounded-lg border p-4 ${
                  rel.void_release
                    ? 'border-red-200 dark:border-red-900/40 opacity-60'
                    : 'dark:border-gray-700'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                        Line {rel.line_num} / Rel {rel.release_num}
                      </span>
                      {rel.void_release ? (
                        <span className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                          VOIDED
                        </span>
                      ) : rel.open ? (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                          Open
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                          Closed
                        </span>
                      )}
                      {rel.firm && !rel.void_release && (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">
                          Firm
                        </span>
                      )}
                    </div>
                    {!rel.void_release && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Need By: <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(rel.need_by_date)}</span>
                      </div>
                    )}
                  </div>

                  {rel.void_release ? (
                    <div className="mt-3 text-xs text-red-500 dark:text-red-400">
                      Release voided — excluded from shipment schedule
                    </div>
                  ) : (
                    <div className="flex items-center gap-6 mt-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Qty: </span>
                        <span className="font-medium">{formatQty(rel.order_qty)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Shipped: </span>
                        <span className="font-medium text-green-600 dark:text-green-400">{formatQty(rel.shipped_qty)}</span>
                      </div>
                      {rel.job_num && (
                        <div className="flex items-center gap-1 text-amtrend-blue">
                          <Wrench size={14} />
                          <span className="font-medium">{rel.job_num}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No releases found for this order
              </div>
            )}
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="space-y-3">
            {jobs && jobs.length > 0 ? (
              jobs.map((job, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wrench size={16} className="text-amtrend-blue" />
                      <span className="font-medium text-amtrend-blue">{job.job_num}</span>
                      {job.closed ? (
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">Closed</span>
                      ) : job.complete ? (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">Complete</span>
                      ) : job.released ? (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">Released</span>
                      ) : null}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Line {job.order_line}{job.release_num ? ` / Rel ${job.release_num}` : ''}
                    </span>
                  </div>
                  {(job.prod_qty > 0 || job.qty_completed > 0) && (
                    <div className="flex items-center gap-6 mt-2 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Prod Qty: </span>
                        <span className="font-medium">{formatQty(job.prod_qty)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Completed: </span>
                        <span className="font-medium text-green-600 dark:text-green-400">{formatQty(job.qty_completed)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No jobs linked to this order
              </div>
            )}
          </div>
        )}

        {/* Shipping Tab */}
        {activeTab === 'shipping' && (
          <div className="space-y-4">
            {/* Ship-To Address */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Package size={16} />
                Ship-To Address
                {ship_to.is_ots && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded">One-Time Ship-To</span>
                )}
              </h3>
              {ship_to.name ? (
                <div className="text-sm">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{ship_to.name}</p>
                  <p className="text-gray-600 dark:text-gray-400">{ship_to.address1}</p>
                  {ship_to.address2 && <p className="text-gray-600 dark:text-gray-400">{ship_to.address2}</p>}
                  {ship_to.address3 && <p className="text-gray-600 dark:text-gray-400">{ship_to.address3}</p>}
                  <p className="text-gray-600 dark:text-gray-400">
                    {ship_to.city}, {ship_to.state} {ship_to.zip}
                  </p>
                  {ship_to.country && ship_to.country !== 'US' && ship_to.country !== 'USA' && (
                    <p className="text-gray-600 dark:text-gray-400">{ship_to.country}</p>
                  )}
                  {ship_to.contact && (
                    <p className="text-gray-500 dark:text-gray-400 mt-2">Contact: {ship_to.contact}</p>
                  )}
                  {ship_to.phone && (
                    <p className="text-gray-500 dark:text-gray-400">Phone: {ship_to.phone}</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">Same as customer address</p>
              )}
            </div>

            {/* Shipping Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Shipping Details</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Ship Via</p>
                  <p className="font-medium">{header.ship_via || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">FOB</p>
                  <p className="font-medium">{header.fob || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Terms</p>
                  <p className="font-medium">{header.terms_code || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Sales Rep</p>
                  <p className="font-medium">{header.sales_rep || '-'}</p>
                </div>
              </div>
            </div>

            {/* Order Comment */}
            {header.comment && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 p-4">
                <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Order Comment
                </h3>
                <p className="text-sm text-yellow-900 dark:text-yellow-200">{header.comment}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Errors Warning */}
      {data.partial_data && data.data_errors && (
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800 text-xs text-yellow-700 dark:text-yellow-300">
          <span className="font-medium">⚠️ Partial data:</span> Some information couldn't be loaded
        </div>
      )}
    </div>
  )
}

/**
 * Job Detail Artifact - comprehensive work order view with BOO/BOM
 */
function JobDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatHours = (val) => {
    if (!val && val !== 0) return '-'
    return `${val.toFixed(1)}h`
  }

  const formatPercent = (val) => {
    if (!val && val !== 0) return '-'
    return `${val.toFixed(0)}%`
  }

  // Status badge colors
  const getStatusColor = (color) => {
    switch (color) {
      case 'green': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'blue': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'yellow': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'orange': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      case 'red': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      case 'gray': return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, order, customer, operations, materials, costs, summary } = data

  // Determine if job is over budget (positive variance means actual > estimated = over budget)
  const isOverBudget = (costs?.variance?.total || 0) > 0
  const estTotal = costs?.estimated?.total || summary?.est_cost || 0
  const actTotal = costs?.actual?.total || summary?.act_cost || 0
  const variancePercent = estTotal > 0
    ? ((actTotal - estTotal) / estTotal * 100).toFixed(1)
    : 0

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Job Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status_color)}`}>
              {status.status}
            </span>
            {status.released && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                Released
              </span>
            )}
            {status.late && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 flex items-center gap-1">
                <AlertTriangle size={12} />
                Late
              </span>
            )}
            {data.potentially_incorrect_costing && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 flex items-center gap-1">
                <AlertTriangle size={12} />
                Pre-12/1 Costing
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {order.order_num && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Order #<span className="font-medium text-amtrend-blue">{order.order_num}</span>
                <span className="text-gray-400 dark:text-gray-500 ml-1">Line {order.order_line}</span>
              </span>
            )}
            <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
          </div>
        </div>

        {/* Part Info */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100">{header.part_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.description}</p>
        </div>

        {/* Customer & Dates */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Building2 size={12} />
              Customer
            </div>
            <p className="font-medium truncate" title={customer.name}>{customer.name || '-'}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Start Date
            </div>
            <p className="font-medium">{formatDate(header.start_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Clock size={12} />
              Due Date
            </div>
            <p className={`font-medium ${status.late ? 'text-red-600 dark:text-red-400' : ''}`}>{formatDate(header.due_date)}</p>
          </div>
        </div>

        {/* Quantity Info */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Required: </span>
            <span className="font-medium">{formatQty(header.prod_qty)}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Completed: </span>
            <span className="font-medium text-green-600 dark:text-green-400">{formatQty(header.qty_completed)}</span>
          </div>
          {header.scrap_qty > 0 && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Scrap: </span>
              <span className="font-medium text-red-600 dark:text-red-400">{formatQty(header.scrap_qty)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Cost Summary */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Est. Cost</p>
            <p className="font-bold text-lg text-gray-700 dark:text-gray-300">{formatCurrency(estTotal)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Actual Cost</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(actTotal)}</p>
          </div>
          <div className={`text-center p-2 rounded-lg ${isOverBudget ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <p className="text-xs text-gray-500 dark:text-gray-400">Variance</p>
            <p className={`font-bold text-lg ${isOverBudget ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatCurrency(costs?.variance?.total || (actTotal - estTotal))}
            </p>
          </div>
          <div className={`text-center p-2 rounded-lg ${isOverBudget ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <p className="text-xs text-gray-500 dark:text-gray-400">Var %</p>
            <div className="flex items-center justify-center gap-1">
              {isOverBudget ? (
                <TrendingUp size={14} className="text-red-600 dark:text-red-400" />
              ) : (
                <TrendingDown size={14} className="text-green-600 dark:text-green-400" />
              )}
              <p className={`font-bold text-lg ${isOverBudget ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {variancePercent}%
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Completion Progress</span>
            <span>{header.progress_pct || 0}% complete</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${status.late ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(header.progress_pct || 0, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'operations', 'materials', 'costs'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'operations' && `Operations (${summary.operations_total || 0})`}
            {tab === 'materials' && `Materials (${summary.materials_total || 0})`}
            {tab === 'costs' && 'Costs'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Job Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Wrench size={16} />
                Job Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Job Number</p>
                  <p className="font-medium">{header.job_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Part Number</p>
                  <p className="font-medium">{header.part_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Plant</p>
                  <p className="font-medium">{header.plant || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Quote</p>
                  <p className="font-medium">{order.quote_num ? `#${order.quote_num}` : '-'}</p>
                </div>
              </div>
            </div>

            {/* Hours Summary */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Clock size={16} />
                Hours Summary
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Est. Hours</p>
                  <p className="font-bold">{formatHours(summary.est_hours)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Actual Hours</p>
                  <p className="font-bold">{formatHours(summary.act_hours)}</p>
                </div>
                <div className={`text-center p-2 rounded ${(summary.act_hours || 0) > (summary.est_hours || 0) ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Hours Var</p>
                  <p className={`font-bold ${(summary.act_hours || 0) > (summary.est_hours || 0) ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatHours((summary.est_hours || 0) - (summary.act_hours || 0))}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Operations */}
            {operations && operations.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Settings size={16} />
                  Top Operations
                </h3>
                <div className="space-y-2">
                  {operations.slice(0, 5).map((op, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-6">{op.seq}</span>
                        <span className="font-medium">{op.op_code}</span>
                        <span className="text-gray-500 dark:text-gray-400 truncate max-w-[150px]">{op.description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {op.complete && (
                          <span className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">Done</span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatHours(op.act_hours || 0)}/{formatHours(op.est_hours)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Operations Tab (BOO) */}
        {activeTab === 'operations' && (
          <div className="space-y-3">
            {operations && operations.length > 0 ? (
              operations.map((op, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                        {op.seq}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{op.op_code}</span>
                      {op.complete && (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                          Complete
                        </span>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-medium text-gray-700 dark:text-gray-300">{op.work_center || '-'}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{op.description}</p>

                  {/* Hours Breakdown */}
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Est Hours</p>
                      <p className="font-medium text-sm">{formatHours(op.est_hours)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Act Hours</p>
                      <p className="font-medium text-sm">{formatHours(op.act_hours)}</p>
                    </div>
                  </div>

                  {/* Efficiency */}
                  {op.efficiency !== null && op.efficiency !== undefined && (
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Efficiency:</span>
                      <span className={`font-medium ${op.efficiency >= 100 ? 'text-green-600 dark:text-green-400' : op.efficiency >= 80 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatPercent(op.efficiency)}
                      </span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No operations found for this job
              </div>
            )}
          </div>
        )}

        {/* Materials Tab (BOM) */}
        {activeTab === 'materials' && (
          <div className="space-y-3">
            {materials && materials.length > 0 ? (
              materials.map((mtl, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded">
                        {mtl.seq}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{mtl.part_num}</span>
                      {mtl.complete && (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                          Issued
                        </span>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <p className={`font-medium ${(mtl.required_qty - mtl.issued_qty) > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                        {formatQty(mtl.issued_qty)} / {formatQty(mtl.required_qty)}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">{mtl.description}</p>

                  {/* Cost & Quantity Details */}
                  <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Unit Cost</p>
                      <p className="font-medium">{formatCurrency(mtl.unit_cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Required</p>
                      <p className="font-medium">{formatQty(mtl.required_qty)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Issued</p>
                      <p className="font-medium text-green-600 dark:text-green-400">{formatQty(mtl.issued_qty)}</p>
                    </div>
                  </div>

                  {/* Extended Cost */}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>Ext Cost: <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(mtl.ext_cost)}</span></span>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No materials found for this job
              </div>
            )}
          </div>
        )}

        {/* Costs Tab */}
        {activeTab === 'costs' && (
          <div className="space-y-4">
            {/* Cost Breakdown */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <DollarSign size={16} />
                Cost Breakdown
              </h3>
              <div className="space-y-3">
                {/* Header Row */}
                <div className="grid grid-cols-4 gap-3 text-xs text-gray-500 dark:text-gray-400 font-medium pb-2 border-b">
                  <div>Category</div>
                  <div className="text-right">Estimated</div>
                  <div className="text-right">Actual</div>
                  <div className="text-right">Variance</div>
                </div>

                {/* Material */}
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Boxes size={14} className="text-gray-400" />
                    Material
                  </div>
                  <div className="text-right">{formatCurrency(costs?.estimated?.material)}</div>
                  <div className="text-right">{formatCurrency(costs?.actual?.material)}</div>
                  <div className={`text-right font-medium ${(costs?.variance?.material || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(costs?.variance?.material)}
                  </div>
                </div>

                {/* Labor */}
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-gray-400" />
                    Labor
                  </div>
                  <div className="text-right">{formatCurrency(costs?.estimated?.labor)}</div>
                  <div className="text-right">{formatCurrency(costs?.actual?.labor)}</div>
                  <div className={`text-right font-medium ${(costs?.variance?.labor || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(costs?.variance?.labor)}
                  </div>
                </div>

                {/* Burden */}
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Settings size={14} className="text-gray-400" />
                    Burden
                  </div>
                  <div className="text-right">{formatCurrency(costs?.estimated?.burden)}</div>
                  <div className="text-right">{formatCurrency(costs?.actual?.burden)}</div>
                  <div className={`text-right font-medium ${(costs?.variance?.burden || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(costs?.variance?.burden)}
                  </div>
                </div>

                {/* Subcontract */}
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-gray-400" />
                    Subcontract
                  </div>
                  <div className="text-right">{formatCurrency(costs?.estimated?.subcontract)}</div>
                  <div className="text-right">{formatCurrency(costs?.actual?.subcontract)}</div>
                  <div className={`text-right font-medium ${(costs?.variance?.subcontract || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(costs?.variance?.subcontract)}
                  </div>
                </div>

                {/* Totals */}
                <div className="grid grid-cols-4 gap-3 text-sm font-bold pt-2 border-t">
                  <div>Total</div>
                  <div className="text-right">{formatCurrency(costs?.estimated?.total)}</div>
                  <div className="text-right">{formatCurrency(costs?.actual?.total)}</div>
                  <div className={`text-right ${(costs?.variance?.total || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(costs?.variance?.total)}
                  </div>
                </div>
              </div>
            </div>

            {/* Costing Rates Note */}
            {costs?.costing_rates && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                  <DollarSign size={16} />
                  Costing Rates
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Labor: ${costs.costing_rates.labor_rate}/hr • Burden: ${costs.costing_rates.burden_rate}/hr • Total: ${costs.costing_rates.total_burdened_rate}/hr
                </p>
                {costs.costing_rates.note && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{costs.costing_rates.note}</p>
                )}
              </div>
            )}

            {/* Variance Analysis */}
            {isOverBudget && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4">
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center gap-2">
                  <TrendingUp size={16} />
                  Over Budget Alert
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300">
                  This job is {Math.abs(variancePercent)}% over budget ({formatCurrency(Math.abs(costs?.variance?.total || 0))}).
                  {(costs?.variance?.labor || 0) > 0 && ' Labor costs are a significant contributor.'}
                  {(costs?.variance?.material || 0) > 0 && ' Material costs are a significant contributor.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Errors Warning */}
      {data.partial_data && data.data_errors && (
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800 text-xs text-yellow-700 dark:text-yellow-300">
          <span className="font-medium">⚠️ Partial data:</span> Some information couldn't be loaded
        </div>
      )}
    </div>
  )
}

/**
 * Part Detail Artifact - comprehensive part master view with inventory
 */
function PartDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Type badge colors
  const getTypeColor = (type) => {
    switch (type) {
      case 'M': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'P': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'K': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      case 'S': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, uom, tracking, inventory, costs, where_used, transactions, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Part Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(header.type_code)}`}>
            {header.type_label}
          </span>
          {header.active ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Active
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              Inactive
            </span>
          )}
          {header.non_stock && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              Non-Stock
            </span>
          )}
          {tracking.track_lots && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              Lot Tracked
            </span>
          )}
          {tracking.track_serial && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
              Serial Tracked
            </span>
          )}
          <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
        </div>

        {/* Part Info */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{header.part_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.description}</p>
        </div>

        {/* Classification */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Layers size={12} />
              Class
            </div>
            <p className="font-medium">{header.class_id || '-'}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Hash size={12} />
              Prod Code
            </div>
            <p className="font-medium">{header.prod_code || '-'}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Package size={12} />
              UOM
            </div>
            <p className="font-medium">{uom.inventory || '-'}</p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">On Hand</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatQty(inventory.total_on_hand)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Std Cost</p>
            <p className="font-bold text-lg text-gray-700 dark:text-gray-300">{formatCurrency(costs.standard)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Unit Price</p>
            <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(costs.unit_price)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Inv Value</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{formatCurrency(inventory.value_at_std)}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'inventory', 'costs', 'whereused'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'inventory' && `Inventory (${inventory.location_count})`}
            {tab === 'costs' && 'Costs'}
            {tab === 'whereused' && `Where Used (${where_used.count})`}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Part Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Boxes size={16} />
                Part Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Part Number</p>
                  <p className="font-medium">{header.part_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Type</p>
                  <p className="font-medium">{header.type_label}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Class</p>
                  <p className="font-medium">{header.class_id || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Product Code</p>
                  <p className="font-medium">{header.prod_code || '-'}</p>
                </div>
              </div>
            </div>

            {/* UOM Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Package size={16} />
                Units of Measure
              </h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Class</p>
                  <p className="font-medium">{uom.class || '-'}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Inventory</p>
                  <p className="font-medium">{uom.inventory || '-'}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Purchase</p>
                  <p className="font-medium">{uom.purchase || '-'}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sales</p>
                  <p className="font-medium">{uom.sales || '-'}</p>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            {transactions.recent && transactions.recent.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Clock size={16} />
                  Recent Transactions (Last {transactions.days} days)
                </h3>
                <div className="space-y-2">
                  {transactions.recent.slice(0, 5).map((txn, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(txn.date)}</span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${txn.qty >= 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                          {txn.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${txn.qty >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {txn.qty >= 0 ? '+' : ''}{formatQty(txn.qty)}
                        </span>
                        {txn.warehouse && <span className="text-xs text-gray-400 dark:text-gray-500">{txn.warehouse}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-4">
            {/* Inventory Summary */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Inventory Summary</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total On Hand</p>
                  <p className="font-bold text-lg">{formatQty(inventory.total_on_hand)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Value @ Std</p>
                  <p className="font-bold text-lg">{formatCurrency(inventory.value_at_std)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Locations</p>
                  <p className="font-bold text-lg">{inventory.location_count}</p>
                </div>
              </div>
            </div>

            {/* By Warehouse */}
            <div className="space-y-3">
              {inventory.warehouses && inventory.warehouses.length > 0 ? (
                inventory.warehouses.map((wh, idx) => (
                  <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{wh.warehouse}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">({wh.bin_count} bins)</span>
                      </div>
                      <span className="font-bold text-lg">{formatQty(wh.qty)}</span>
                    </div>
                    {wh.bins && wh.bins.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        {wh.bins.map((bin, binIdx) => (
                          <div key={binIdx} className="flex items-center justify-between text-sm p-1.5 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                            <span className="text-gray-600 dark:text-gray-400">{bin.bin || 'No Bin'}</span>
                            <span className="font-medium">{formatQty(bin.qty)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                  No inventory found for this part
                </div>
              )}
            </div>
          </div>
        )}

        {/* Costs Tab */}
        {activeTab === 'costs' && (
          <div className="space-y-4">
            {/* Cost Summary */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <DollarSign size={16} />
                Cost Summary
              </h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Standard</p>
                  <p className="font-bold text-lg text-blue-700 dark:text-blue-300">{formatCurrency(costs.standard)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Average</p>
                  <p className="font-bold text-lg">{formatCurrency(costs.average)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Last</p>
                  <p className="font-bold text-lg">{formatCurrency(costs.last)}</p>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Unit Price</p>
                  <p className="font-bold text-lg text-green-700 dark:text-green-400">{formatCurrency(costs.unit_price)}</p>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            {costs.breakdown && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Standard Cost Breakdown</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <div className="flex items-center gap-2">
                      <Boxes size={14} className="text-gray-400" />
                      Material
                    </div>
                    <span className="font-medium">{formatCurrency(costs.breakdown.material)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-400" />
                      Labor
                    </div>
                    <span className="font-medium">{formatCurrency(costs.breakdown.labor)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <div className="flex items-center gap-2">
                      <Settings size={14} className="text-gray-400" />
                      Burden
                    </div>
                    <span className="font-medium">{formatCurrency(costs.breakdown.burden)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-gray-400" />
                      Subcontract
                    </div>
                    <span className="font-medium">{formatCurrency(costs.breakdown.subcontract)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-gray-400" />
                      Mtl Burden
                    </div>
                    <span className="font-medium">{formatCurrency(costs.breakdown.mtl_burden)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-2 bg-blue-50 dark:bg-blue-900/20 rounded font-bold border-t">
                    <span>Total Standard</span>
                    <span>{formatCurrency(costs.standard)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Margin Analysis */}
            {costs.unit_price > 0 && costs.standard > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Margin Analysis</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Margin $</p>
                    <p className={`font-bold text-lg ${costs.unit_price - costs.standard >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatCurrency(costs.unit_price - costs.standard)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Margin %</p>
                    <p className={`font-bold text-lg ${((costs.unit_price - costs.standard) / costs.unit_price * 100) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {((costs.unit_price - costs.standard) / costs.unit_price * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Where Used Tab */}
        {activeTab === 'whereused' && (
          <div className="space-y-3">
            {where_used.parents && where_used.parents.length > 0 ? (
              where_used.parents.map((parent, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{parent.parent_part}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{parent.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatQty(parent.qty_per)} per</p>
                      {parent.revision && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">Rev: {parent.revision}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                This part is not used in any BOMs
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Errors Warning */}
      {data.partial_data && data.data_errors && (
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800 text-xs text-yellow-700 dark:text-yellow-300">
          <span className="font-medium">⚠️ Partial data:</span> Some information couldn't be loaded
        </div>
      )}
    </div>
  )
}

/**
 * Invoice Detail Artifact - AR invoice view with lines and payments
 */
function InvoiceDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Open': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'Overdue': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  // Invoice type labels
  const getTypeLabel = (type) => {
    switch (type) {
      case 'INV': return 'Invoice'
      case 'CM': return 'Credit Memo'
      case 'DM': return 'Debit Memo'
      case 'DEP': return 'Deposit'
      case 'MIS': return 'Miscellaneous'
      default: return type || 'Invoice'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, lines, payments, related, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Invoice Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
            {status.status}
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-surface-dark-overlay text-gray-800 dark:text-gray-200">
            {getTypeLabel(header.invoice_type)}
          </span>
          {status.is_overdue && status.days_overdue > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {status.days_overdue} days overdue
            </span>
          )}
          <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
        </div>

        {/* Invoice Number & Customer */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Invoice #{header.invoice_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.customer_name} ({header.cust_id})</p>
        </div>

        {/* Key Info */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Invoice Date
            </div>
            <p className="font-medium">{formatDate(header.invoice_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Clock size={12} />
              Due Date
            </div>
            <p className={`font-medium ${status.is_overdue ? 'text-red-600 dark:text-red-400' : ''}`}>{formatDate(header.due_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <FileText size={12} />
              PO #
            </div>
            <p className="font-medium">{header.po_num || '-'}</p>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Invoice Total</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(header.invoice_amt)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Paid</p>
            <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(payments.total_paid)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
            <p className={`font-bold text-lg ${header.invoice_bal > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatCurrency(header.invoice_bal)}
            </p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">% Paid</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{payments.percent_paid}%</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'lines', 'payments'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'lines' && `Lines (${lines.count})`}
            {tab === 'payments' && `Payments (${payments.count})`}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Invoice Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <FileText size={16} />
                Invoice Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Invoice Number</p>
                  <p className="font-medium">{header.invoice_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Type</p>
                  <p className="font-medium">{getTypeLabel(header.invoice_type)}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Customer</p>
                  <p className="font-medium">{header.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Customer ID</p>
                  <p className="font-medium">{header.cust_id}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Terms</p>
                  <p className="font-medium">{header.terms_code || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Ship Date</p>
                  <p className="font-medium">{formatDate(header.ship_date)}</p>
                </div>
              </div>
            </div>

            {/* Payment Progress */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <DollarSign size={16} />
                Payment Progress
              </h3>
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${payments.percent_paid}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Paid: {formatCurrency(payments.total_paid)}</span>
                  <span className={`font-medium ${header.invoice_bal > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    Remaining: {formatCurrency(payments.remaining)}
                  </span>
                </div>
              </div>
            </div>

            {/* Related Orders */}
            {related.has_orders && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Package size={16} />
                  Related Orders
                </h3>
                <div className="flex flex-wrap gap-2">
                  {related.orders.map((orderNum, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                      Order #{orderNum}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Summary</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Line Items</span>
                  <span className="font-medium">{summary.line_count}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Payments</span>
                  <span className="font-medium">{summary.payment_count}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Fully Paid</span>
                  <span className={`font-medium ${summary.fully_paid ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {summary.fully_paid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Discounts Applied</span>
                  <span className="font-medium">{summary.has_discount ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lines Tab */}
        {activeTab === 'lines' && (
          <div className="space-y-3">
            {lines.items && lines.items.length > 0 ? (
              lines.items.map((line, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-2 py-0.5 rounded">
                        Line {line.line}
                      </span>
                      {line.part_num && (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{line.part_num}</span>
                      )}
                    </div>
                    <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(line.ext_price)}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{line.description}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Qty:</span>
                      <span className="ml-1 font-medium">{formatQty(line.qty)} {line.uom}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Unit:</span>
                      <span className="ml-1 font-medium">{formatCurrency(line.unit_price)}</span>
                    </div>
                    {line.order_num && line.order_num > 0 && (
                      <div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">Order:</span>
                        <span className="ml-1 font-medium">{line.order_num}-{line.order_line}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No line items found
              </div>
            )}

            {/* Lines Total */}
            {lines.items && lines.items.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300">Total ({lines.count} lines, {formatQty(lines.total_qty)} units)</span>
                <span className="font-bold text-lg">{formatCurrency(header.invoice_amt)}</span>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-4">
            {/* Payment Summary */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Payment Summary</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Paid</p>
                  <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(payments.total_paid)}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
                  <p className={`font-bold text-lg ${payments.remaining > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(payments.remaining)}
                  </p>
                </div>
                <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Payments</p>
                  <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{payments.count}</p>
                </div>
              </div>
            </div>

            {/* Payment List */}
            <div className="space-y-3">
              {payments.items && payments.items.length > 0 ? (
                payments.items.map((payment, idx) => (
                  <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <DollarSign size={16} className="text-green-500 dark:text-green-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {payment.check_ref ? `Check #${payment.check_ref}` : payment.receipt_num ? `Receipt #${payment.receipt_num}` : 'Payment Confirmed'}
                        </span>
                        {payment.posted && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-xs rounded">Posted</span>
                        )}
                        {payment.linkage === 'inferred' && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs rounded">Matched</span>
                        )}
                        {payment.linkage === 'derived' && (
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-xs rounded">Via Balance</span>
                        )}
                      </div>
                      <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(payment.applied_amt)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                      {payment.payment_date && (
                        <div>
                          <span className="text-xs text-gray-400 dark:text-gray-500">Date:</span>
                          <span className="ml-1">{formatDate(payment.payment_date)}</span>
                        </div>
                      )}
                      {payment.discount_amt > 0 && (
                        <div>
                          <span className="text-xs text-gray-400 dark:text-gray-500">Discount:</span>
                          <span className="ml-1 text-green-600 dark:text-green-400">{formatCurrency(payment.discount_amt)}</span>
                        </div>
                      )}
                    </div>
                    {payment.note && (
                      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">{payment.note}</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                  No payments recorded yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Purchase Order Detail Artifact - PO view with lines, releases, and receipts
 */
function PODetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed': return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
      case 'Fully Received': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Partially Received': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'Open': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'Pending Approval': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, lines, releases, receipts, totals, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* PO Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
            {status.status}
          </span>
          {header.approved ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Approved
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              Pending Approval
            </span>
          )}
          {status.has_late_lines && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {status.late_line_count} Late
            </span>
          )}
          <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
        </div>

        {/* PO Number & Vendor */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">PO #{header.po_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.vendor_name} ({header.vendor_id})</p>
        </div>

        {/* Key Info */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Order Date
            </div>
            <p className="font-medium">{formatDate(header.order_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <User size={12} />
              Buyer
            </div>
            <p className="font-medium">{header.buyer_id || '-'}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Truck size={12} />
              Ship Via
            </div>
            <p className="font-medium">{header.ship_via || '-'}</p>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">PO Total</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(totals.total_ordered)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Qty Ordered</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{formatQty(totals.qty_ordered)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Qty Received</p>
            <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatQty(totals.qty_received)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">% Received</p>
            <p className="font-bold text-lg text-purple-600 dark:text-purple-400">{totals.receive_percent}%</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'lines', 'releases', 'receipts'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'lines' && `Lines (${lines.count})`}
            {tab === 'releases' && `Releases (${releases.count})`}
            {tab === 'receipts' && `Receipts (${receipts.count})`}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* PO Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <ShoppingCart size={16} />
                Purchase Order Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">PO Number</p>
                  <p className="font-medium">{header.po_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Vendor</p>
                  <p className="font-medium">{header.vendor_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Vendor ID</p>
                  <p className="font-medium">{header.vendor_id}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Buyer</p>
                  <p className="font-medium">{header.buyer_id || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Terms</p>
                  <p className="font-medium">{header.terms || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">FOB</p>
                  <p className="font-medium">{header.fob || '-'}</p>
                </div>
              </div>
            </div>

            {/* Receipt Progress */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Package size={16} />
                Receipt Progress
              </h3>
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${totals.receive_percent}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Received: {formatQty(totals.qty_received)}</span>
                  <span className={`font-medium ${totals.qty_remaining > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                    Remaining: {formatQty(totals.qty_remaining)}
                  </span>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Summary</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Lines</span>
                  <span className="font-medium">{summary.line_count} ({lines.open_count} open)</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Releases</span>
                  <span className="font-medium">{summary.release_count} ({releases.open_count} open)</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Receipts</span>
                  <span className="font-medium">{summary.receipt_count}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Fully Received</span>
                  <span className={`font-medium ${summary.fully_received ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {summary.fully_received ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lines Tab */}
        {activeTab === 'lines' && (
          <div className="space-y-3">
            {lines.items && lines.items.length > 0 ? (
              lines.items.map((line, idx) => (
                <div key={idx} className={`bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 ${line.is_late ? 'border-red-300' : ''}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-2 py-0.5 rounded">
                        Line {line.line}
                      </span>
                      {line.part_num && (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{line.part_num}</span>
                      )}
                      {line.is_late && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded">Late</span>
                      )}
                      {!line.open && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-surface-dark-overlay text-gray-600 dark:text-gray-400 rounded">Closed</span>
                      )}
                    </div>
                    <span className="font-bold text-gray-700 dark:text-gray-300">{formatCurrency(line.ext_cost)}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{line.description}</p>
                  <div className="grid grid-cols-4 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Ordered:</span>
                      <span className="ml-1 font-medium">{formatQty(line.qty_ordered)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Received:</span>
                      <span className="ml-1 font-medium text-green-600 dark:text-green-400">{formatQty(line.qty_received)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Remaining:</span>
                      <span className={`ml-1 font-medium ${line.qty_remaining > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                        {formatQty(line.qty_remaining)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Due:</span>
                      <span className={`ml-1 ${line.is_late ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                        {formatDate(line.due_date)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No lines found
              </div>
            )}

            {/* Lines Total */}
            {lines.items && lines.items.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300">Total ({lines.count} lines)</span>
                <span className="font-bold text-lg">{formatCurrency(totals.total_ordered)}</span>
              </div>
            )}
          </div>
        )}

        {/* Releases Tab */}
        {activeTab === 'releases' && (
          <div className="space-y-3">
            {releases.items && releases.items.length > 0 ? (
              releases.items.map((rel, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-2 py-0.5 rounded">
                        Line {rel.line} / Rel {rel.release}
                      </span>
                      {rel.job_num && (
                        <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Job: {rel.job_num}</span>
                      )}
                      {!rel.open && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded">Complete</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(rel.due_date)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Ordered</p>
                      <p className="font-medium">{formatQty(rel.qty)}</p>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Received</p>
                      <p className="font-medium text-green-600 dark:text-green-400">{formatQty(rel.qty_received)}</p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded text-center">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Remaining</p>
                      <p className={`font-medium ${rel.qty_remaining > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                        {formatQty(rel.qty_remaining)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No releases found
              </div>
            )}
          </div>
        )}

        {/* Receipts Tab */}
        {activeTab === 'receipts' && (
          <div className="space-y-3">
            {receipts.items && receipts.items.length > 0 ? (
              receipts.items.map((rec, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-green-500 dark:text-green-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Pack Slip: {rec.pack_slip}</span>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(rec.receipt_date)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No receipts recorded yet
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * AP Invoice Detail Artifact - Accounts Payable invoice view with discount info
 */
function APInvoiceDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Open': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'Overdue': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, discount, lines, payment, related, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Invoice Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
            {status.status}
          </span>
          {status.is_overdue && status.days_overdue > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {status.days_overdue} days overdue
            </span>
          )}
          {discount.available && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Discount Available
            </span>
          )}
          <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
        </div>

        {/* Invoice Number & Vendor */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">AP Invoice #{header.invoice_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.vendor_name} ({header.vendor_id})</p>
        </div>

        {/* Key Info */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Invoice Date
            </div>
            <p className="font-medium">{formatDate(header.invoice_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Clock size={12} />
              Due Date
            </div>
            <p className={`font-medium ${status.is_overdue ? 'text-red-600 dark:text-red-400' : ''}`}>{formatDate(header.due_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <ShoppingCart size={12} />
              PO #
            </div>
            <p className="font-medium">{header.po_num || '-'}</p>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Invoice Total</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{formatCurrency(header.invoice_amt)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Paid</p>
            <p className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(payment.paid_amt)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
            <p className={`font-bold text-lg ${header.invoice_bal > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatCurrency(header.invoice_bal)}
            </p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">% Paid</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{payment.percent_paid}%</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'lines', 'discount'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'lines' && `Lines (${lines.count})`}
            {tab === 'discount' && 'Discount'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Invoice Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <FileText size={16} />
                Invoice Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Invoice Number</p>
                  <p className="font-medium">{header.invoice_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Vendor</p>
                  <p className="font-medium">{header.vendor_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Vendor ID</p>
                  <p className="font-medium">{header.vendor_id}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Terms</p>
                  <p className="font-medium">{header.terms_code || '-'}</p>
                </div>
              </div>
            </div>

            {/* Payment Progress */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <DollarSign size={16} />
                Payment Progress
              </h3>
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${payment.percent_paid}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Paid: {formatCurrency(payment.paid_amt)}</span>
                  <span className={`font-medium ${header.invoice_bal > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    Remaining: {formatCurrency(payment.remaining)}
                  </span>
                </div>
              </div>
            </div>

            {/* Discount Alert */}
            {discount.available && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4">
                <h3 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center gap-2">
                  <DollarSign size={16} />
                  Discount Available!
                </h3>
                <p className="text-sm text-green-600 dark:text-green-400">
                  Save <span className="font-bold">{formatCurrency(discount.amount)}</span> if paid by {formatDate(discount.date)}
                  {discount.expires_in_days <= 3 && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                      (Expires in {discount.expires_in_days} day{discount.expires_in_days !== 1 ? 's' : ''}!)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Related POs */}
            {related.has_pos && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <ShoppingCart size={16} />
                  Related Purchase Orders
                </h3>
                <div className="flex flex-wrap gap-2">
                  {related.pos.map((poNum, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                      PO #{poNum}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Summary</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Line Items</span>
                  <span className="font-medium">{summary.line_count}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Fully Paid</span>
                  <span className={`font-medium ${payment.fully_paid ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {payment.fully_paid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Days Until Due</span>
                  <span className={`font-medium ${status.is_overdue ? 'text-red-600 dark:text-red-400' : ''}`}>
                    {status.is_overdue ? `${status.days_overdue} overdue` : status.days_until_due}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Has Discount</span>
                  <span className={`font-medium ${summary.discount_available ? 'text-green-600 dark:text-green-400' : ''}`}>
                    {summary.has_discount ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lines Tab */}
        {activeTab === 'lines' && (
          <div className="space-y-3">
            {lines.items && lines.items.length > 0 ? (
              lines.items.map((line, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-2 py-0.5 rounded">
                        Line {line.line}
                      </span>
                      {line.part_num && (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{line.part_num}</span>
                      )}
                    </div>
                    <span className="font-bold text-red-600 dark:text-red-400">{formatCurrency(line.ext_cost)}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{line.description}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Qty:</span>
                      <span className="ml-1 font-medium">{formatQty(line.qty)} {line.uom}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Unit:</span>
                      <span className="ml-1 font-medium">{formatCurrency(line.unit_cost)}</span>
                    </div>
                    {line.po_num && line.po_num > 0 && (
                      <div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">PO:</span>
                        <span className="ml-1 font-medium">{line.po_num}-{line.po_line}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No line items found
              </div>
            )}

            {/* Lines Total */}
            {lines.items && lines.items.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300">Total ({lines.count} lines)</span>
                <span className="font-bold text-lg">{formatCurrency(header.invoice_amt)}</span>
              </div>
            )}
          </div>
        )}

        {/* Discount Tab */}
        {activeTab === 'discount' && (
          <div className="space-y-4">
            {/* Discount Info */}
            <div className={`rounded-lg border p-4 ${discount.available ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-white dark:bg-surface-dark-raised'}`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${discount.available ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                <DollarSign size={16} />
                Discount Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Discount Amount</p>
                  <p className={`font-bold text-lg ${discount.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {formatCurrency(discount.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Discount Date</p>
                  <p className="font-medium">{formatDate(discount.date)}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Status</p>
                  <p className={`font-medium ${discount.available ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {discount.available ? 'Available' : discount.amount > 0 ? 'Expired' : 'None'}
                  </p>
                </div>
                {discount.available && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Expires In</p>
                    <p className={`font-medium ${discount.expires_in_days <= 3 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                      {discount.expires_in_days} day{discount.expires_in_days !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Comparison */}
            {discount.amount > 0 && status.is_open && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Payment Comparison</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                    <span className="text-gray-600 dark:text-gray-400">Pay Full Amount</span>
                    <span className="font-bold">{formatCurrency(header.invoice_bal)}</span>
                  </div>
                  {discount.available && (
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                      <span className="text-green-700 dark:text-green-400">Pay with Discount</span>
                      <div className="text-right">
                        <span className="font-bold text-green-700 dark:text-green-400">{formatCurrency(header.invoice_bal - discount.amount)}</span>
                        <span className="text-xs text-green-600 dark:text-green-400 ml-2">Save {formatCurrency(discount.amount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Shipment Detail Artifact - Pack slip/BOL view with tracking and lines
 */
function ShipmentDetailArtifact({ data }) {
  const [activeTab, setActiveTab] = useState('overview')

  const formatQty = (val) => {
    if (val === null || val === undefined) return '-'
    return val.toLocaleString()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatWeight = (val, uom = 'LB') => {
    if (val === null || val === undefined || val === 0) return '-'
    return `${val.toLocaleString()} ${uom}`
  }

  const formatCurrency = (val) => {
    if (!val) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
  }

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Shipped': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'Invoiced': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'Voided': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-surface-dark-overlay dark:text-gray-200'
    }
  }

  if (data.error) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        <AlertTriangle size={48} className="mx-auto mb-2" />
        <p>{data.error}</p>
      </div>
    )
  }

  const { header, status, tracking, lines, related, summary } = data

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark">
      {/* Shipment Header Card */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
            {status.status}
          </span>
          {status.shipped && !status.invoiced && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              Not Invoiced
            </span>
          )}
          {status.shipped_known === false && (
            <span
              title={status.status_source || 'ShipHead.ShipStatus is blank or unrecognized'}
              className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700 dark:bg-surface-dark-overlay dark:text-gray-300">
              Ship status unknown
            </span>
          )}
          {tracking.has_tracking && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Has Tracking
            </span>
          )}
          <EpicorOpenButtons entryUrl={data.epicor_entry_url} trackerUrl={data.epicor_tracker_url} />
        </div>

        {/* Pack Number & Customer */}
        <div className="mb-3">
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">Pack Slip #{header.pack_num}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{header.customer_name}</p>
        </div>

        {/* Key Info */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Calendar size={12} />
              Ship Date
            </div>
            <p className="font-medium">{formatDate(header.ship_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Truck size={12} />
              Ship Via
            </div>
            <p className="font-medium">{header.ship_via || '-'}</p>
          </div>
          <div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
              <Package size={12} />
              Order #
            </div>
            <p className="font-medium">{header.order_num || '-'}</p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Lines</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{summary.line_count}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Qty</p>
            <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{formatQty(lines.total_qty)}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Packages</p>
            <p className="font-bold text-lg text-purple-600 dark:text-purple-400">{summary.package_count}</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Weight</p>
            <p className="font-bold text-lg text-gray-700 dark:text-gray-300">{formatWeight(summary.total_weight, header.weight_uom)}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b dark:border-gray-700 bg-white dark:bg-surface-dark-raised">
        {['overview', 'lines', 'tracking'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amtrend-blue text-amtrend-blue'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'lines' && `Lines (${lines.count})`}
            {tab === 'tracking' && 'Tracking'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Shipment Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Truck size={16} />
                Shipment Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Pack Slip</p>
                  <p className="font-medium">{header.pack_num}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Customer</p>
                  <p className="font-medium">{header.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Ship To #</p>
                  <p className="font-medium">{header.ship_to_num || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Ship Via</p>
                  <p className="font-medium">{header.ship_via || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Weight</p>
                  <p className="font-medium">{formatWeight(header.weight, header.weight_uom)}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Packages</p>
                  <p className="font-medium">{header.num_packages || 1}</p>
                </div>
              </div>
            </div>

            {/* Status Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Package size={16} />
                Status
              </h3>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Shipped</span>
                  <span
                    title={status.ship_status ? `ShipStatus: ${status.ship_status}` : status.status_source}
                    className={`font-medium ${status.shipped ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {status.shipped_known === false ? 'Unknown' : (status.shipped ? 'Yes' : 'No')}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Invoiced</span>
                  <span className={`font-medium ${status.invoiced ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                    {status.invoiced ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-surface-dark-overlay rounded">
                  <span className="text-gray-600 dark:text-gray-400">Voided</span>
                  <span className={`font-medium ${status.voided ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {status.voided ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Related Orders/Jobs */}
            {(related.has_orders || related.has_jobs) && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Related Documents</h3>
                <div className="space-y-2">
                  {related.has_orders && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Orders</p>
                      <div className="flex flex-wrap gap-2">
                        {related.orders.map((orderNum, idx) => (
                          <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                            #{orderNum}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {related.has_jobs && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Jobs</p>
                      <div className="flex flex-wrap gap-2">
                        {related.jobs.map((jobNum, idx) => (
                          <span key={idx} className="px-3 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                            {jobNum}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lines Tab */}
        {activeTab === 'lines' && (
          <div className="space-y-3">
            {lines.items && lines.items.length > 0 ? (
              lines.items.map((line, idx) => (
                <div key={idx} className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay px-2 py-0.5 rounded">
                        Line {line.line}
                      </span>
                      {line.part_num && (
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{line.part_num}</span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-blue-600 dark:text-blue-400">{formatQty(line.qty_shipped)} {line.uom}</span>
                      {line.ext_price > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatCurrency(line.ext_price)}
                          {line.unit_price > 0 && ` (${formatCurrency(line.unit_price)} ea)`}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{line.description}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    {line.order_num && (
                      <div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">Order:</span>
                        <span className="ml-1 font-medium">{line.order_num}-{line.order_line}-{line.order_rel}</span>
                      </div>
                    )}
                    {line.job_num && (
                      <div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">Job:</span>
                        <span className="ml-1 font-medium text-purple-600 dark:text-purple-400">{line.job_num}</span>
                      </div>
                    )}
                    {line.lot_num && (
                      <div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">Lot:</span>
                        <span className="ml-1 font-medium">{line.lot_num}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 text-center text-gray-500 dark:text-gray-400">
                No lines found
              </div>
            )}

            {/* Lines Total */}
            {lines.items && lines.items.length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4 flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300">Total ({lines.count} lines)</span>
                <div className="text-right">
                  <span className="font-bold text-lg">{formatQty(lines.total_qty)} units</span>
                  {lines.total_value > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatCurrency(lines.total_value)} est. invoice value
                      {lines.unpriced_lines > 0 && ` · ${lines.unpriced_lines} unpriced line(s) excluded`}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tracking Tab */}
        {activeTab === 'tracking' && (
          <div className="space-y-4">
            {/* Carrier Info */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Truck size={16} />
                Carrier Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Carrier/Ship Via</p>
                  <p className="font-medium">{tracking.carrier || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Ship Date</p>
                  <p className="font-medium">{formatDate(header.ship_date)}</p>
                </div>
              </div>
            </div>

            {/* Tracking Numbers */}
            <div className="bg-white dark:bg-surface-dark-raised rounded-lg border dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Tracking Numbers</h3>
              {tracking.has_tracking ? (
                <div className="space-y-2">
                  {/* Use tracking.items if available (has URLs from BAQ), fall back to tracking.numbers */}
                  {(tracking.items && tracking.items.length > 0
                    ? tracking.items
                    : tracking.numbers.map(n => ({ number: n, url: '', carrier: tracking.carrier }))
                  ).map((item, idx) => {
                    // Use URL from BAQ if available, otherwise build from carrier
                    const trackNum = typeof item === 'string' ? item : item.number
                    let trackingUrl = typeof item === 'object' ? item.url : ''

                    // If no URL from BAQ, try to build one from carrier
                    if (!trackingUrl && trackNum) {
                      const carrier = ((typeof item === 'object' ? item.carrier : tracking.carrier) || '').toUpperCase()
                      if (carrier.includes('UPS')) {
                        trackingUrl = `https://www.ups.com/track?tracknum=${trackNum}`
                      } else if (carrier.includes('FEDEX') || carrier.includes('FED')) {
                        trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackNum}`
                      } else if (carrier.includes('USPS')) {
                        trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackNum}`
                      } else if (carrier.includes('DHL')) {
                        trackingUrl = `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackNum}`
                      }
                      // LTL carriers (BEST, ABF, ESTES, SAIA, XPO) - no universal URL
                    }

                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Package size={16} className="text-blue-500 dark:text-blue-400" />
                          {trackingUrl ? (
                            <a
                              href={trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 hover:underline"
                            >
                              {trackNum}
                            </a>
                          ) : (
                            <span className="font-mono font-medium text-blue-700 dark:text-blue-300">{trackNum}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {typeof item === 'object' && item.carrier && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{item.carrier}</span>
                          )}
                          <span className="text-xs text-blue-600 dark:text-blue-400">Package {idx + 1}</span>
                          {trackingUrl && (
                            <a
                              href={trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                            >
                              Track
                            </a>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 dark:text-gray-400">
                  No tracking numbers recorded
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Main Artifact Panel Component
 * Supports minimize/maximize to preserve state while getting panel out of the way
 * Supports resizable width via drag handle
 */
export default function ArtifactPanel({
  artifact,
  artifacts = [],
  activeIndex = 0,
  onSelectArtifact,
  onCloseArtifact,
  onUpdateArtifact,
  getArtifactLabel,
  isMinimized,
  onMinimize,
  onClose,
  onEmailRequest
}) {
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('trendy_artifact_panel_width')
      const w = saved ? parseInt(saved, 10) : 480
      return (w >= 360 && w <= 1000) ? w : 480
    } catch { return 480 }
  })
  const [isResizing, setIsResizing] = useState(false)
  const minWidth = 360
  const maxWidth = 1000
  const hasMultipleArtifacts = artifacts.length > 1

  // Handle resize drag
  const handleResizeStart = (e) => {
    e.preventDefault()
    setIsResizing(true)

    const startX = e.clientX
    const startWidth = panelWidth

    const handleMouseMove = (moveEvent) => {
      // Moving left increases width (panel is on the right)
      const delta = startX - moveEvent.clientX
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta))
      setPanelWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      // Persist width on drag end
      setPanelWidth(w => {
        try { localStorage.setItem('trendy_artifact_panel_width', String(w)) } catch {}
        return w
      })
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // ─── Relationship Graph Artifact ────────────────────────────────
  // Force-directed node-link diagram for Bill-To networks, part hierarchies, etc.
  const RelationshipGraph = ({ data }) => {
    const nodes = data.nodes || []
    const edges = data.edges || []
    const title = data.title || 'Entity Relationships'
    const [positions, setPositions] = useState(null)

    // Simple force layout on mount
    useEffect(() => {
      if (!nodes.length) return
      const width = 700, height = 500
      const centerX = width / 2, centerY = height / 2
      // Initialize positions in a circle
      const pos = {}
      nodes.forEach((node, i) => {
        const angle = (2 * Math.PI * i) / nodes.length
        const radius = Math.min(width, height) * 0.35
        pos[node.id] = {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
          vx: 0, vy: 0
        }
      })

      // Run simple force simulation (50 iterations)
      for (let iter = 0; iter < 80; iter++) {
        // Repulsion between all nodes
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const a = pos[nodes[i].id], b = pos[nodes[j].id]
            const dx = b.x - a.x, dy = b.y - a.y
            const dist = Math.sqrt(dx * dx + dy * dy) || 1
            const force = 8000 / (dist * dist)
            const fx = (dx / dist) * force, fy = (dy / dist) * force
            a.vx -= fx; a.vy -= fy
            b.vx += fx; b.vy += fy
          }
        }
        // Attraction along edges
        edges.forEach(e => {
          const a = pos[e.source], b = pos[e.target]
          if (!a || !b) return
          const dx = b.x - a.x, dy = b.y - a.y
          const dist = Math.sqrt(dx * dx + dy * dy) || 1
          const force = (dist - 120) * 0.02
          const fx = (dx / dist) * force, fy = (dy / dist) * force
          a.vx += fx; a.vy += fy
          b.vx -= fx; b.vy -= fy
        })
        // Center gravity
        nodes.forEach(n => {
          const p = pos[n.id]
          p.vx += (centerX - p.x) * 0.005
          p.vy += (centerY - p.y) * 0.005
        })
        // Apply velocities with damping
        nodes.forEach(n => {
          const p = pos[n.id]
          p.x += p.vx * 0.3
          p.y += p.vy * 0.3
          p.vx *= 0.85
          p.vy *= 0.85
          // Bounds
          p.x = Math.max(60, Math.min(width - 60, p.x))
          p.y = Math.max(40, Math.min(height - 40, p.y))
        })
      }
      setPositions(pos)
    }, [nodes, edges])

    if (!positions || !nodes.length) {
      return <div className="p-8 text-center text-gray-500">No relationship data</div>
    }

    const nodeColorMap = { parent: '#6366f1', child: '#06b6d4', hub: '#f59e0b', default: '#94a3b8' }
    const width = 700, height = 500

    return (
      <div className="p-4 animate-in fade-in duration-500">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        {data.subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{data.subtitle}</p>}
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
            </marker>
            <filter id="node-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
            </filter>
          </defs>
          {/* Edges */}
          {edges.map((e, i) => {
            const s = positions[e.source], t = positions[e.target]
            if (!s || !t) return null
            const midX = (s.x + t.x) / 2, midY = (s.y + t.y) / 2 - 10
            return (
              <g key={`edge-${i}`}>
                <line x1={s.x} y1={s.y} x2={t.x} y2={t.y}
                  stroke="#cbd5e1" strokeWidth={1.5} markerEnd="url(#arrowhead)"
                  className="dark:stroke-gray-600" />
                {e.label && (
                  <text x={midX} y={midY} textAnchor="middle" className="text-[9px] fill-gray-400 dark:fill-gray-500">{e.label}</text>
                )}
              </g>
            )
          })}
          {/* Nodes */}
          {nodes.map((node, i) => {
            const p = positions[node.id]
            if (!p) return null
            const color = nodeColorMap[node.role || 'default'] || nodeColorMap.default
            const r = node.size || (node.role === 'parent' || node.role === 'hub' ? 28 : 20)
            return (
              <g key={`node-${i}`} filter="url(#node-shadow)" className="cursor-pointer">
                <circle cx={p.x} cy={p.y} r={r} fill={color} opacity={0.9} />
                <circle cx={p.x} cy={p.y} r={r} fill="none" stroke="white" strokeWidth={2} opacity={0.3} />
                <text x={p.x} y={p.y + r + 14} textAnchor="middle"
                  className="text-[10px] font-medium fill-gray-700 dark:fill-gray-300">{node.label}</text>
                {node.detail && (
                  <text x={p.x} y={p.y + r + 25} textAnchor="middle"
                    className="text-[8px] fill-gray-400 dark:fill-gray-500">{node.detail}</text>
                )}
                {node.value && (
                  <text x={p.x} y={p.y + 4} textAnchor="middle"
                    className="text-[9px] font-bold fill-white">{node.value}</text>
                )}
              </g>
            )
          })}
        </svg>
        {/* Legend */}
        {data.legend && (
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
            {data.legend.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color || nodeColorMap[item.role] || '#94a3b8' }} />
                {item.label}
              </span>
            ))}
          </div>
        )}
        {data.stats && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            {Object.entries(data.stats).map(([k, v], i) => (
              <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-center">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{k}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{v}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ─── Workflow Diagram Artifact ────────────────────────────────────
  // Horizontal step flow with status indicators per step
  const WorkflowDiagram = ({ data }) => {
    const steps = data.steps || []
    const title = data.title || 'Workflow'

    const statusConfig = {
      complete: { color: '#10b981', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: '✓' },
      active: { color: '#3b82f6', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: '●' },
      pending: { color: '#94a3b8', bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', icon: '○' },
      error: { color: '#ef4444', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: '✕' },
      skipped: { color: '#d4d4d8', bg: 'bg-gray-50 dark:bg-gray-800/50', text: 'text-gray-400 dark:text-gray-500', icon: '–' },
    }

    return (
      <div className="p-5 animate-in fade-in duration-500">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</h3>
        {data.subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{data.subtitle}</p>}

        {/* Horizontal flow */}
        <div className="flex items-start gap-0 overflow-x-auto pb-4 pt-2">
          {steps.map((step, i) => {
            const cfg = statusConfig[step.status || 'pending'] || statusConfig.pending
            return (
              <div key={i} className="flex items-start flex-shrink-0">
                <div className="flex flex-col items-center w-28" style={{ animationDelay: `${i * 100}ms` }}>
                  {/* Circle */}
                  <div className={`w-12 h-12 rounded-full ${cfg.bg} flex items-center justify-center border-2 transition-all duration-500`}
                    style={{ borderColor: cfg.color }}>
                    {step.status === 'active' ? (
                      <div className="w-4 h-4 rounded-full animate-pulse" style={{ backgroundColor: cfg.color }} />
                    ) : (
                      <span className="text-base font-bold" style={{ color: cfg.color }}>{cfg.icon}</span>
                    )}
                  </div>
                  {/* Label */}
                  <p className={`text-xs font-semibold mt-2 text-center ${cfg.text}`}>{step.label}</p>
                  {/* Detail */}
                  {step.detail && <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-0.5">{step.detail}</p>}
                  {/* Date */}
                  {step.date && <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">{step.date}</p>}
                  {/* Value */}
                  {step.value && <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mt-0.5">{step.value}</p>}
                </div>
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="flex items-center mt-5 mx-0.5">
                    <div className="w-8 h-0.5 transition-all duration-500"
                      style={{ backgroundColor: step.status === 'complete' ? '#10b981' : '#e2e8f0' }} />
                    <div className="w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[6px]"
                      style={{ borderLeftColor: step.status === 'complete' ? '#10b981' : '#e2e8f0' }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Summary stats */}
        {data.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {Object.entries(data.summary).map(([k, v], i) => (
              <div key={i} className="text-center">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{k}</p>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{v}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ─── What-If Calculator Artifact ─────────────────────────────────
  // Interactive calculator with live-updating results
  const WhatIfCalculator = ({ data }) => {
    const title = data.title || 'What-If Calculator'
    const inputs = data.inputs || []
    const [values, setValues] = useState(() => {
      const init = {}
      inputs.forEach(inp => { init[inp.key] = inp.default || 0 })
      return init
    })

    // Calculate derived values using the formula definitions
    const results = useMemo(() => {
      const formulas = data.formulas || []
      const computed = { ...values }
      formulas.forEach(f => {
        try {
          // Simple formula evaluation using function constructor
          const fn = new Function(...Object.keys(computed), `return ${f.expression}`)
          computed[f.key] = fn(...Object.values(computed))
        } catch {
          computed[f.key] = 0
        }
      })
      return computed
    }, [values, data.formulas])

    const outputItems = data.outputs || []

    const handleChange = (key, val) => {
      setValues(prev => ({ ...prev, [key]: parseFloat(val) || 0 }))
    }

    const formatOutput = (val, format) => {
      if (format === 'currency') {
        if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(2)}M`
        if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(1)}K`
        return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      if (format === 'percent') return `${val.toFixed(1)}%`
      if (format === 'hours') return `${val.toFixed(1)} hrs`
      return val.toLocaleString(undefined, { maximumFractionDigits: 2 })
    }

    return (
      <div className="p-5 animate-in fade-in duration-500">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</h3>
        {data.subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{data.subtitle}</p>}

        {/* Inputs */}
        <div className="space-y-4 mb-6">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Adjust Parameters</p>
          {inputs.map((inp, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{inp.label}</label>
                <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                  {formatOutput(values[inp.key] || 0, inp.format)}
                </span>
              </div>
              <input
                type="range"
                min={inp.min || 0}
                max={inp.max || 100}
                step={inp.step || 1}
                value={values[inp.key] || 0}
                onChange={(e) => handleChange(inp.key, e.target.value)}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              />
              <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                <span>{formatOutput(inp.min || 0, inp.format)}</span>
                <span>{formatOutput(inp.max || 100, inp.format)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Results */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Results</p>
          <div className="grid grid-cols-2 gap-3">
            {outputItems.map((out, i) => {
              const val = results[out.key] || 0
              const isGood = out.goodWhen === 'high' ? val > (out.threshold || 0) :
                out.goodWhen === 'low' ? val < (out.threshold || Infinity) : true
              return (
                <div key={i} className={`rounded-xl p-4 border transition-all duration-300 ${
                  isGood
                    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
                }`}>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{out.label}</p>
                  <p className={`text-xl font-bold ${isGood ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {formatOutput(val, out.format)}
                  </p>
                  {out.description && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{out.description}</p>}
                </div>
              )
            })}
          </div>
        </div>

        {/* Presets */}
        {data.presets && data.presets.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Quick Presets</p>
            <div className="flex flex-wrap gap-2">
              {data.presets.map((preset, i) => (
                <button key={i}
                  onClick={() => setValues(prev => ({ ...prev, ...preset.values }))}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── Chart Artifact ─────────────────────────────────────────────
  // Production-grade interactive Recharts visualizations.
  // Supports: bar, stacked_bar, line, scatter, heatmap, horizontal_bar
  // Claude returns: {type:"chart", chartType:"bar", title, data, series, axes, colors}
  // Premium color palette — rich gradients and distinct hues
  const CHART_COLORS = ['#6366f1', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#84cc16']

  // Smart color assignment for aging/category data
  const AGING_PALETTE = { 'Current': '#10b981', '1-30': '#3b82f6', '1-30 Days': '#3b82f6', '31-60': '#f59e0b', '31-60 Days': '#f59e0b', '61-90': '#f97316', '61-90 Days': '#f97316', '90+': '#ef4444', '90+ Days': '#ef4444' }

  // Assign colors per bar based on data label (aging-aware)
  const getBarColor = (entry, xKey, fallback, index) => {
    const label = entry[xKey] || ''
    if (AGING_PALETTE[label]) return AGING_PALETTE[label]
    // Gradient effect: assign from palette by index
    return CHART_COLORS[index % CHART_COLORS.length]
  }

  const ChartArtifact = ({ data, onDrillDown }) => {
    const chartType = data.chartType || data.chart_type || 'bar'
    const title = data.title || 'Chart'
    const subtitle = data.subtitle || null
    const chartData = data.data || []
    const series = data.series || []
    const axes = data.axes || {}
    const colors = data.colors || CHART_COLORS
    const height = data.height || 380
    const [clickedIndex, setClickedIndex] = useState(null)

    const drillDownEnabled = data.drillDown?.enabled && onDrillDown
    const handleChartClick = useCallback((dataPoint, seriesKey) => {
      if (!drillDownEnabled || !data.drillDown?.prompt_template) return
      let prompt = data.drillDown.prompt_template
      if (dataPoint) {
        for (const [key, val] of Object.entries(dataPoint)) {
          prompt = prompt.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val ?? ''))
        }
        if (seriesKey) prompt = prompt.replace(/\{series\}/g, seriesKey)
      }
      const idx = chartData.indexOf(dataPoint)
      setClickedIndex(idx)
      setTimeout(() => { setClickedIndex(null); onDrillDown(prompt) }, 200)
    }, [drillDownEnabled, data.drillDown, chartData, onDrillDown])

    // Auto-detect series from data keys if not specified
    const detectedSeries = series.length > 0 ? series : (() => {
      if (!chartData.length) return []
      const keys = Object.keys(chartData[0]).filter(k =>
        k !== (axes.x || 'name') && k !== 'label' && k !== 'id' && typeof chartData[0][k] === 'number'
      )
      return keys.map((k, i) => ({ dataKey: k, name: k.replace(/_/g, ' '), color: colors[i % colors.length] }))
    })()

    const xKey = axes.x || 'name'
    const formatValue = (v) => {
      if (typeof v !== 'number') return v
      if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
      if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}K`
      if (axes.yFormat === 'percent') return `${v.toFixed(1)}%`
      if (axes.yFormat === 'currency') return `$${v.toLocaleString()}`
      return v.toLocaleString()
    }

    const formatLabel = (v) => {
      if (typeof v !== 'number') return v
      if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
      if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}K`
      return `$${v.toLocaleString()}`
    }

    // Premium tooltip with glassmorphism effect
    const CustomTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null
      return (
        <div className="backdrop-blur-xl bg-gray-900/90 text-white text-xs rounded-xl px-4 py-3 shadow-2xl border border-white/10">
          <p className="font-bold text-sm mb-1.5 text-white/90">{label}</p>
          {payload.map((p, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-white/70">{p.name}:</span>
              <span className="font-bold text-white ml-auto">{formatLabel(p.value)}</span>
            </div>
          ))}
        </div>
      )
    }

    const renderChart = () => {
      switch (chartType) {
        case 'bar':
        case 'stacked_bar':
          return (
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: chartData.length > 6 ? 80 : 40 }} barCategoryGap="20%">
                <defs>
                  {CHART_COLORS.map((c, i) => (
                    <linearGradient key={i} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey={xKey}
                  tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  angle={chartData.length > 6 ? -40 : 0}
                  textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                  height={chartData.length > 6 ? 80 : 40}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatValue}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
                {detectedSeries.length > 1 && (
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    iconType="circle"
                    iconSize={8}
                  />
                )}
                {detectedSeries.map((s, i) => (
                  <Bar
                    key={s.dataKey}
                    dataKey={s.dataKey}
                    name={s.name || s.dataKey}
                    stackId={chartType === 'stacked_bar' ? 'stack' : undefined}
                    radius={chartType === 'stacked_bar' ? [2, 2, 0, 0] : [6, 6, 0, 0]}
                    style={drillDownEnabled ? { cursor: 'pointer' } : undefined}
                    onClick={drillDownEnabled ? (barData) => handleChartClick(barData, s.dataKey) : undefined}
                    animationDuration={1200}
                    animationEasing="ease-out"
                    animationBegin={0}
                  >
                    {chartData.map((entry, idx) => {
                      const color = detectedSeries.length === 1
                        ? getBarColor(entry, xKey, s.color || colors[i], idx)
                        : (s.color || colors[i % colors.length])
                      const isClicked = clickedIndex === idx
                      return <Cell key={idx} fill={color} opacity={isClicked ? 0.4 : 1} />
                    })}
                  </Bar>
                ))}
                {data.referenceLine && (
                  <ReferenceLine
                    y={data.referenceLine.value}
                    stroke={data.referenceLine.color || '#ef4444'}
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    label={{ value: data.referenceLine.label, position: 'right', fill: '#ef4444', fontSize: 11, fontWeight: 600 }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          )

        case 'horizontal_bar': {
          // Calculate Y-axis width based on longest label (min 120, max 200)
          const maxLabelLen = chartData.reduce((max, d) => Math.max(max, String(d[xKey] || '').length), 0)
          const yAxisWidth = Math.min(200, Math.max(120, maxLabelLen * 6.5))
          return (
            <ResponsiveContainer width="100%" height={Math.max(height, chartData.length * 48)}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 10 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={formatValue} />
                <YAxis type="category" dataKey={xKey}
                  tick={({ x, y, payload }) => {
                    const label = String(payload.value || '')
                    const maxChars = Math.floor(yAxisWidth / 6.5)
                    const truncated = label.length > maxChars ? label.slice(0, maxChars - 1) + '…' : label
                    return (
                      <text x={x - 4} y={y} dy={4} textAnchor="end" fontSize={11} fill="#475569" fontWeight={500}>
                        {truncated}
                      </text>
                    )
                  }}
                  axisLine={false} tickLine={false} width={yAxisWidth} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
                {detectedSeries.map((s, i) => (
                  <Bar
                    key={s.dataKey}
                    dataKey={s.dataKey}
                    name={s.name || s.dataKey}
                    radius={[0, 6, 6, 0]}
                    style={drillDownEnabled ? { cursor: 'pointer' } : undefined}
                    onClick={drillDownEnabled ? (barData) => handleChartClick(barData, s.dataKey) : undefined}
                    animationDuration={800}
                  >
                    {chartData.map((entry, idx) => {
                      let fill = s.color || colors[i % colors.length]
                      if (data.colorByValue) {
                        const val = entry[s.dataKey]
                        const threshold = data.threshold || 100
                        fill = val > threshold ? '#ef4444' : '#10b981'
                      }
                      return <Cell key={idx} fill={fill} opacity={clickedIndex === idx ? 0.4 : 1} />
                    })}
                  </Bar>
                ))}
                {data.referenceLine && (
                  <ReferenceLine x={data.referenceLine.value} stroke={data.referenceLine.color || '#ef4444'} strokeDasharray="6 4" strokeWidth={2} />
                )}
              </BarChart>
            </ResponsiveContainer>
          )
        }

        case 'line':
          return (
            <ResponsiveContainer width="100%" height={height}>
              <LineChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <defs>
                  {detectedSeries.map((s, i) => (
                    <linearGradient key={i} id={`lineGrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={s.color || colors[i]} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={s.color || colors[i]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={formatValue} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" iconSize={8} />
                {detectedSeries.map((s, i) => (
                  <Line
                    key={s.dataKey}
                    type="monotone"
                    dataKey={s.dataKey}
                    name={s.name || s.dataKey}
                    stroke={s.color || colors[i % colors.length]}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#fff', stroke: s.color || colors[i], strokeWidth: 2, style: drillDownEnabled ? { cursor: 'pointer' } : undefined }}
                    activeDot={drillDownEnabled ? {
                      r: 7, fill: s.color || colors[i], stroke: '#fff', strokeWidth: 2,
                      style: { cursor: 'pointer', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' },
                      onClick: (e, payload) => handleChartClick(payload?.payload, s.dataKey)
                    } : { r: 6, fill: s.color || colors[i], stroke: '#fff', strokeWidth: 2 }}
                    animationDuration={1000}
                  />
                ))}
                {data.referenceLine && (
                  <ReferenceLine y={data.referenceLine.value} stroke={data.referenceLine.color || '#ef4444'} strokeDasharray="6 4" strokeWidth={2} label={{ value: data.referenceLine.label, position: 'right', fill: '#ef4444', fontSize: 11, fontWeight: 600 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )

        case 'scatter':
        case 'bubble':
          return (
            <ResponsiveContainer width="100%" height={height}>
              <ScatterChart margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey={axes.x || 'x'}
                  name={axes.xLabel || axes.x || 'X'}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={formatValue}
                />
                <YAxis
                  dataKey={axes.y || 'y'}
                  name={axes.yLabel || axes.y || 'Y'}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={formatValue}
                />
                {chartType === 'bubble' && (
                  <ZAxis dataKey={axes.z || 'z'} range={[40, 400]} />
                )}
                <Tooltip content={<CustomTooltip />} />
                <Scatter
                  data={chartData}
                  fill="#3b82f6"
                  style={drillDownEnabled ? { cursor: 'pointer' } : undefined}
                  onClick={drillDownEnabled ? (scatterData) => handleChartClick(scatterData, null) : undefined}
                >
                  {chartData.map((entry, idx) => {
                    const yVal = entry[axes.y || 'y']
                    const color = yVal > 50 ? '#10b981' : yVal > 25 ? '#f59e0b' : '#ef4444'
                    const isClicked = clickedIndex === idx
                    return <Cell key={idx} fill={data.colorByValue ? color : colors[idx % colors.length]} opacity={isClicked ? 0.5 : 1} />
                  })}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )

        case 'heatmap':
          // Render as a grid of colored cells (no Recharts heatmap — CSS grid)
          const rows = data.rows || []
          const cols = data.columns || []
          const values = data.values || [] // 2D array [row][col]
          const maxVal = data.maxValue || Math.max(...values.flat().filter(v => typeof v === 'number'))
          const getHeatColor = (v) => {
            const pct = maxVal > 0 ? v / maxVal : 0
            if (pct > 0.9) return 'bg-red-500 text-white'
            if (pct > 0.7) return 'bg-orange-400 text-white'
            if (pct > 0.5) return 'bg-yellow-400 text-gray-900'
            if (pct > 0.3) return 'bg-green-300 text-gray-900'
            return 'bg-green-100 text-gray-700'
          }
          return (
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-gray-500 font-medium"></th>
                    {cols.map((c, i) => (
                      <th key={i} className="px-2 py-1 text-center text-gray-500 font-medium whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>
                      <td className="px-2 py-1 font-medium text-gray-700 whitespace-nowrap">{row}</td>
                      {(values[ri] || []).map((val, ci) => (
                        <td key={ci} className={`px-2 py-2 text-center font-mono rounded ${getHeatColor(val)}`}>
                          {typeof val === 'number' ? `${val.toFixed(0)}%` : val || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-400 justify-end">
                <span>Low</span>
                <div className="flex gap-0.5">
                  <div className="w-4 h-3 bg-green-100 rounded-sm" />
                  <div className="w-4 h-3 bg-green-300 rounded-sm" />
                  <div className="w-4 h-3 bg-yellow-400 rounded-sm" />
                  <div className="w-4 h-3 bg-orange-400 rounded-sm" />
                  <div className="w-4 h-3 bg-red-500 rounded-sm" />
                </div>
                <span>High</span>
              </div>
            </div>
          )

        default:
          return <GenericDataArtifact data={data} />
      }
    }

    // Parse summary — supports both flat values and {value, status, format} objects
    const parseSummaryItem = (val) => {
      if (val === null || val === undefined) return { display: '-', status: null }
      if (typeof val === 'object' && val !== null && val.value !== undefined) {
        return { display: String(val.value), status: val.status }
      }
      if (typeof val === 'number') return { display: formatValue(val), status: null }
      // Handle arrays — show count instead of [object Object] spam
      if (Array.isArray(val)) {
        return { display: `${val.length} items`, status: null }
      }
      // Handle nested objects — show key count instead of [object Object]
      if (typeof val === 'object') {
        return { display: `${Object.keys(val).length} fields`, status: null }
      }
      return { display: String(val), status: null }
    }

    const fadeInStyle = (delay = 0) => ({
      animation: `fadeSlideIn 0.4s ease-out ${delay}s both`,
    })

    return (
      <div className="p-6 space-y-5">
        <style>{`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Header */}
        <div style={fadeInStyle(0)}>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>

        {/* Chart area */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm" style={fadeInStyle(0.1)}>
          {chartData.length > 0 || (data.rows && data.rows.length > 0) ? renderChart() : (
            <p className="text-center text-gray-400 py-12">No data to visualize</p>
          )}
        </div>

        {/* Summary KPI cards — staggered fade-in */}
        {data.summary && (
          <div className={`grid gap-3 ${Object.keys(data.summary).length <= 2 ? 'grid-cols-2' : Object.keys(data.summary).length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
            {Object.entries(data.summary).map(([key, val], cardIdx) => {
              const { display, status } = parseSummaryItem(val)
              const statusColor = status === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                status === 'success' ? 'text-emerald-600 dark:text-emerald-400' :
                status === 'danger' ? 'text-red-600 dark:text-red-400' :
                'text-gray-900 dark:text-white'
              const borderColor = status === 'warning' ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10' :
                status === 'success' ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10' :
                status === 'danger' ? 'border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10' :
                'border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30'
              return (
                <div
                  key={key}
                  className={`rounded-xl p-4 text-center border ${borderColor} hover:shadow-md hover:scale-[1.02] transition-all duration-200`}
                  style={fadeInStyle(0.2 + cardIdx * 0.08)}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{key.replace(/_/g, ' ')}</p>
                  <p className={`text-xl font-bold ${statusColor} tracking-tight`}>{display}</p>
                </div>
              )
            })}
          </div>
        )}

        {/* Drill-down hint */}
        {drillDownEnabled && (
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 justify-center">
            <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            Click any bar to drill down into details
          </p>
        )}

        {/* Collapsible data table */}
        {chartData.length > 0 && chartType !== 'heatmap' && (
          <DataTableToggle data={chartData} />
        )}
      </div>
    )
  }

  // Collapsible data table for chart artifacts
  const DataTableToggle = ({ data: tableData }) => {
    const [showTable, setShowTable] = useState(false)
    if (!tableData?.length) return null
    const headers = Object.keys(tableData[0])
    return (
      <div>
        <button
          onClick={() => setShowTable(!showTable)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
        >
          {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {showTable ? 'Hide' : 'Show'} data table ({tableData.length} rows)
        </button>
        {showTable && (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-3 py-1.5 text-left text-gray-500 font-medium uppercase tracking-wider whitespace-nowrap">
                      {h.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tableData.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    {headers.map(h => (
                      <td key={h} className="px-3 py-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {typeof row[h] === 'number' ? row[h].toLocaleString() : String(row[h] ?? '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  // ─── Generic Data Artifact ──────────────────────────────────────
  // Fallback renderer for any artifact type not matched by a specific component.
  // Auto-detects arrays (→ table), objects (→ key-value cards), and primitives.
  // Prevents "Unknown artifact type" dead ends when Claude returns new shapes.
  const GenericDataArtifact = ({ data }) => {
    // Extract the actual payload — skip metadata keys
    const META_KEYS = new Set(['type', 'title', 'id', 'timestamp', 'retrievedAt'])
    const payload = {}
    for (const [k, v] of Object.entries(data || {})) {
      if (!META_KEYS.has(k) && v !== null && v !== undefined) payload[k] = v
    }
    const entries = Object.entries(payload)
    const title = data?.title || data?.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Data'

    // Auto-detect if any value is an array of objects (render as table)
    const tableEntry = entries.find(([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object')
    // Scalar/simple values for KPI cards
    const scalarEntries = entries.filter(([, v]) => typeof v !== 'object' || v === null)
    // Nested objects (not arrays)
    const objectEntries = entries.filter(([, v]) => typeof v === 'object' && v !== null && !Array.isArray(v))

    return (
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-700 flex items-center justify-center">
            <Layers size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">{data?.type || 'generic'}</p>
          </div>
        </div>

        {/* Scalar values as KPI cards */}
        {scalarEntries.length > 0 && (
          <div className={`grid gap-3 ${scalarEntries.length <= 2 ? 'grid-cols-2' : scalarEntries.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {scalarEntries.map(([key, val]) => (
              <div key={key} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {key.replace(/_/g, ' ')}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1 break-words">
                  {typeof val === 'number' ? val.toLocaleString() : Array.isArray(val) ? `${val.length} items` : typeof val === 'object' && val !== null ? `${Object.keys(val).length} fields` : String(val)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Array of objects as table */}
        {tableEntry && (() => {
          const [tableName, rows] = tableEntry
          const headers = Object.keys(rows[0])
          return (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 capitalize">
                {tableName.replace(/_/g, ' ')} ({rows.length})
              </h4>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {headers.map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          {h.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.slice(0, 50).map((row, ri) => (
                      <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {headers.map(h => (
                          <td key={h} className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row[h] === null || row[h] === undefined ? '—' :
                              typeof row[h] === 'number' ? row[h].toLocaleString() :
                              typeof row[h] === 'boolean' ? (row[h] ? 'Yes' : 'No') :
                              String(row[h]).length > 60 ? String(row[h]).slice(0, 60) + '...' :
                              String(row[h])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 50 && (
                  <p className="px-3 py-2 text-xs text-gray-400 bg-gray-50 dark:bg-gray-800 text-center">
                    Showing 50 of {rows.length} rows
                  </p>
                )}
              </div>
            </div>
          )
        })()}

        {/* Nested objects as key-value sections */}
        {objectEntries.map(([key, obj]) => (
          <div key={key}>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 capitalize">
              {key.replace(/_/g, ' ')}
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-2">
              {Object.entries(obj).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize whitespace-nowrap">{k.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white text-right break-words">
                    {typeof v === 'number' ? v.toLocaleString() :
                     typeof v === 'boolean' ? (v ? 'Yes' : 'No') :
                     Array.isArray(v) ? v.join(', ') :
                     typeof v === 'object' ? JSON.stringify(v) :
                     String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {entries.length === 0 && (
          <p className="text-center text-gray-400 dark:text-gray-500 text-sm py-4">No data to display</p>
        )}
      </div>
    )
  }

  // ─── Generated Document Artifact ─────────────────────────────────
  // Renders a download card for PDF documents generated via chat.
  // Downloads from GET /api/documents/pending/{file_id} (30-min TTL).
  const GeneratedDocumentArtifact = ({ data }) => {
    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState(null)

    const metadata = data.metadata || {}
    const filename = data.filename || 'document.pdf'
    const toolName = data.tool_name || ''

    // Derive a human-readable document type from the tool name
    const docTypeMap = {
      generate_rfq_document: 'RFQ Document',
      generate_quote_pdf: 'Quote PDF',
      generate_vendor_package: 'Vendor Package',
      generate_comparison_matrix: 'Comparison Matrix',
      generate_ar_statement: 'AR Statement',
      generate_job_traveler: 'Job Traveler',
      generate_packing_list: 'Packing List',
      generate_order_confirmation: 'Order Confirmation',
    }
    const docType = docTypeMap[toolName] || 'Document'

    // Extract interesting metadata fields for display
    const displayMeta = []
    if (metadata.customer_name) displayMeta.push({ label: 'Customer', value: metadata.customer_name })
    if (metadata.cust_id) displayMeta.push({ label: 'Customer ID', value: metadata.cust_id })
    if (metadata.total_ar !== undefined) displayMeta.push({ label: 'Total AR', value: `$${Number(metadata.total_ar).toLocaleString()}` })
    if (metadata.open_invoice_count) displayMeta.push({ label: 'Open Invoices', value: metadata.open_invoice_count })
    if (metadata.quote_num) displayMeta.push({ label: 'Quote #', value: metadata.quote_num })
    if (metadata.order_num) displayMeta.push({ label: 'Order #', value: metadata.order_num })
    if (metadata.job_num) displayMeta.push({ label: 'Job #', value: metadata.job_num })
    if (metadata.pack_num) displayMeta.push({ label: 'Pack #', value: metadata.pack_num })
    if (metadata.vendor_name) displayMeta.push({ label: 'Vendor', value: metadata.vendor_name })
    if (metadata.line_count) displayMeta.push({ label: 'Lines', value: metadata.line_count })
    if (metadata.order_total) displayMeta.push({ label: 'Total', value: `$${Number(metadata.order_total).toLocaleString()}` })
    if (metadata.statement_date) displayMeta.push({ label: 'Date', value: metadata.statement_date })

    const handleDownload = async () => {
      setDownloading(true)
      setDownloadError(null)
      try {
        const token = localStorage.getItem('amtrend_token')
        const res = await fetch(`/api/documents/pending/${data.file_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!res.ok) {
          if (res.status === 404) {
            setDownloadError('File expired. PDFs are available for 30 minutes after generation. Please ask Trendy to regenerate.')
            return
          }
          throw new Error(`Download failed (${res.status})`)
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (err) {
        console.error('PDF download error:', err)
        setDownloadError(err.message || 'Download failed')
      } finally {
        setDownloading(false)
      }
    }

    return (
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg">
            <FileText size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{docType}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{filename}</p>
          </div>
        </div>

        {/* Metadata grid */}
        {displayMeta.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-3">
              {displayMeta.map(({ label, value }, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Download button */}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg ${
            downloading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-amtrend-blue to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl active:scale-[0.98]'
          }`}
        >
          <Download size={18} />
          {downloading ? 'Downloading...' : 'Download PDF'}
        </button>

        {/* Error state */}
        {downloadError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{downloadError}</p>
          </div>
        )}

        {/* Expiration notice */}
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center flex items-center justify-center gap-1">
          <Clock size={12} />
          Available for 30 minutes after generation
        </p>
      </div>
    )
  }

  if (!artifact) return null

  // Determine artifact type and render appropriate component
  const renderArtifact = () => {
    switch (artifact.type) {
      case 'photo_gallery':
      case 'photo_gallery_urls':
        return <PhotoGalleryArtifact data={artifact} onEmailRequest={onEmailRequest} />

      case 'quote_detail':
        return <QuoteDetailArtifact data={artifact} onSendMessage={onEmailRequest} />

      case 'customer_360':
        return <Customer360Artifact data={artifact} />

      case 'order_detail':
        return <OrderDetailArtifact data={artifact} />

      case 'job_detail':
        return <JobDetailArtifact data={artifact} />

      case 'part_detail':
        return <PartDetailArtifact data={artifact} />

      case 'invoice_detail':
        return <InvoiceDetailArtifact data={artifact} />

      case 'po_detail':
        return <PODetailArtifact data={artifact} />

      case 'ap_invoice_detail':
        return <APInvoiceDetailArtifact data={artifact} />

      case 'shipment_detail':
        return <ShipmentDetailArtifact data={artifact} />

      case 'attachment_viewer':
        return (
          <AttachmentViewer
            xfileRefNum={artifact.xfile_ref_num}
            metadata={artifact.metadata}
          />
        )

      case 'attachment_list':
        return (
          <AttachmentList
            attachments={artifact.attachments}
            title={artifact.title || `Attachments for ${artifact.record || 'Record'}`}
            onEmailRequest={onEmailRequest}
            orderNum={artifact.order_num}
            tableName={artifact.table_name}
            recordKey1={artifact.key1}
            recordKey2={artifact.key2}
          />
        )

      case 'generated_document':
        return <GeneratedDocumentArtifact data={artifact} />

      case 'chart':
        return <ChartArtifact data={artifact} onDrillDown={onEmailRequest} />

      case 'relationship_graph':
        return <RelationshipGraph data={artifact} />

      case 'workflow_diagram':
        return <WorkflowDiagram data={artifact} />

      case 'what_if_calculator':
        return <WhatIfCalculator data={artifact} />

      case 'email_compose':
        return (
          <EmailComposeArtifact
            data={artifact}
            onSend={async (payload) => {
              // Pass the user-edited final values (NOT the AI's original draft) back to
              // chat so the AI calls send_email with the actual values from the UI.
              // The structured JSON sub-block lets the AI parse the final values
              // deterministically rather than re-inferring from the original draft.
              const safePayload = {
                to: payload.to || [],
                cc: payload.cc || [],
                bcc: payload.bcc || [],
                subject: payload.subject || '',
                body_html: payload.body_html || '',
              }
              const message =
                'Confirmed — send the email using these final values from the side panel ' +
                '(these are the values the user reviewed and edited; do NOT use the original draft):\n\n' +
                '```json\n' + JSON.stringify(safePayload, null, 2) + '\n```'
              onEmailRequest?.(message)
              // Write back the sent state + final edited values to the persisted artifact
              // so re-mounts (panel minimize/maximize, page reload) show the locked
              // read-only view rather than reverting to the original editable draft.
              onUpdateArtifact?.(activeIndex, {
                status: 'sent',
                to: safePayload.to,
                cc: safePayload.cc,
                bcc: safePayload.bcc,
                subject: safePayload.subject,
                body_html: safePayload.body_html,
                sent_at: new Date().toISOString(),
              })
            }}
            onAutoClose={() => onMinimize?.()}
            onDiscard={() => onCloseArtifact?.(activeIndex)}
          />
        )

      default:
        return <GenericDataArtifact data={artifact} />
    }
  }

  // Get icon based on type
  const getIcon = () => {
    switch (artifact.type) {
      case 'photo_gallery':
      case 'photo_gallery_urls':
        return <Image size={18} />
      case 'quote_detail':
        return <FileText size={18} />
      case 'customer_360':
        return <Building2 size={18} />
      case 'order_detail':
        return <Package size={18} />
      case 'job_detail':
        return <Wrench size={18} />
      case 'part_detail':
        return <Boxes size={18} />
      case 'invoice_detail':
        return <FileText size={18} />
      case 'po_detail':
        return <ShoppingCart size={18} />
      case 'ap_invoice_detail':
        return <FileText size={18} />
      case 'shipment_detail':
        return <Truck size={18} />
      case 'attachment_viewer':
      case 'attachment_list':
        return <Paperclip size={18} />
      case 'generated_document':
        return <Download size={18} />
      case 'chart':
        return <BarChart2 size={18} className="text-emerald-600" />
      case 'relationship_graph':
        return <Layers size={18} className="text-purple-600" />
      case 'workflow_diagram':
        return <Settings size={18} className="text-blue-600" />
      case 'what_if_calculator':
        return <TrendingUp size={18} className="text-amber-600" />
      case 'email_compose':
        return <Mail size={18} className="text-blue-600" />
      case 'table':
        return <Table size={18} />
      default:
        return <Layers size={18} />
    }
  }

  // Get title based on type
  const getTitle = () => {
    switch (artifact.type) {
      case 'photo_gallery':
      case 'photo_gallery_urls':
        return `Photos - Order ${artifact.order_num}`
      case 'quote_detail':
        return `Quote ${artifact.quote_num}`
      case 'customer_360':
        return artifact.customer_name || `Customer ${artifact.cust_num}`
      case 'order_detail':
        return `Order ${artifact.order_num}`
      case 'job_detail':
        return `Job ${artifact.job_num}`
      case 'part_detail':
        return `Part ${artifact.part_num}`
      case 'invoice_detail':
        return `Invoice ${artifact.invoice_num}`
      case 'po_detail':
        return `PO ${artifact.po_num}`
      case 'ap_invoice_detail':
        return `AP Invoice ${artifact.invoice_num}`
      case 'shipment_detail':
        return `Pack ${artifact.pack_num}`
      case 'attachment_viewer':
        return artifact.metadata?.filename || 'Attachment'
      case 'attachment_list':
        return artifact.title || 'Attachments'
      case 'generated_document':
        return artifact.filename?.replace(/_/g, ' ')?.replace('.pdf', '') || 'Document'
      case 'email_compose':
        return artifact.subject || 'New Email'
      default:
        return artifact.title || artifact.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Data'
    }
  }

  // Get short title for minimized state
  const getShortTitle = () => {
    switch (artifact.type) {
      case 'photo_gallery':
      case 'photo_gallery_urls':
        return `Order ${artifact.order_num}`
      case 'quote_detail':
        return `Quote ${artifact.quote_num}`
      case 'customer_360':
        return `#${artifact.cust_num}`
      case 'order_detail':
        return `#${artifact.order_num}`
      case 'job_detail':
        return artifact.job_num
      case 'part_detail':
        return artifact.part_num
      case 'invoice_detail':
        return `#${artifact.invoice_num}`
      case 'po_detail':
        return `#${artifact.po_num}`
      case 'ap_invoice_detail':
        return `AP #${artifact.invoice_num}`
      case 'shipment_detail':
        return `#${artifact.pack_num}`
      case 'attachment_viewer':
        return artifact.metadata?.extension?.toUpperCase() || 'File'
      case 'attachment_list':
        return `${artifact.attachments?.length || 0} files`
      case 'generated_document':
        return 'PDF'
      default:
        return 'Artifact'
    }
  }

  // Minimized state - just a small tab on the side
  if (isMinimized) {
    return (
      <div className="flex flex-col h-full">
        <button
          onClick={onMinimize}
          className="w-12 bg-gradient-to-b from-amtrend-blue to-blue-700 text-white flex flex-col items-center py-4 gap-2 hover:from-blue-700 hover:to-blue-800 transition-colors border-l border-blue-800"
          title={`Expand: ${getTitle()}`}
        >
          <Maximize2 size={18} />
          {/* Show artifact count badge if multiple */}
          {hasMultipleArtifacts && (
            <div className="bg-white/90 text-amtrend-blue text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {artifacts.length}
            </div>
          )}
          <div className="writing-mode-vertical text-xs font-medium whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {getShortTitle()}
          </div>
          <div className="mt-auto">
            {getIcon()}
          </div>
        </button>
      </div>
    )
  }

  // Full panel view
  return (
    <div
      className="border-l dark:border-gray-700 bg-white dark:bg-surface-dark flex flex-col h-full relative"
      style={{ width: `${panelWidth}px` }}
    >
      {/* Resize Handle */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-amtrend-blue/30 transition-colors z-10 ${
          isResizing ? 'bg-amtrend-blue/50' : ''
        }`}
        onMouseDown={handleResizeStart}
        title="Drag to resize"
      />

      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between bg-gradient-to-r from-amtrend-blue to-blue-700">
        <div className="flex items-center gap-3 text-white min-w-0">
          {getIcon()}
          <h3 className="font-semibold truncate">{getTitle()}</h3>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Minimize button */}
          <button
            onClick={onMinimize}
            className="p-1.5 hover:bg-white/20 rounded text-white/80 hover:text-white"
            title="Minimize panel"
          >
            <PanelRightClose size={18} />
          </button>
          {/* Close all button */}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded text-white/80 hover:text-white"
            title={hasMultipleArtifacts ? "Close all artifacts" : "Close panel"}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Artifact Tabs - shown when there are multiple artifacts */}
      {hasMultipleArtifacts && (
        <div className="bg-gray-50 dark:bg-surface-dark border-b dark:border-gray-700 overflow-x-auto flex-shrink-0">
          <div className="flex gap-1 p-1.5 min-w-max">
            {artifacts.map((art, index) => {
              const isActive = index === activeIndex
              const label = getArtifactLabel ? getArtifactLabel(art) : `Artifact ${index + 1}`

              return (
                <div
                  key={index}
                  className={`group flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all ${
                    isActive
                      ? 'bg-white dark:bg-surface-dark-raised shadow-sm text-amtrend-blue border border-gray-200 dark:border-gray-700'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-surface-dark-overlay hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                  onClick={() => onSelectArtifact?.(index)}
                  title={label}
                >
                  <span className="truncate max-w-[100px]">{label}</span>
                  {/* Close individual artifact button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onCloseArtifact?.(index)
                    }}
                    className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-surface-dark-overlay transition-colors ${
                      isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                    title="Close this artifact"
                  >
                    <X size={12} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto scroll-smooth">
        {renderArtifact()}
      </div>
    </div>
  )
}
